// Menu editing — PURE, pack-aware sourcing + edit rules for a single menu run.
//
// This module is the Phase-4 successor to the generator's write-once plan model.
// Instead of computing one net `{decrements, deletes, adds}` plan and discarding
// it, every menu ENTRY durably records HOW it relates to stock so a later edit
// can compute LOCAL stock deltas without double-counting:
//
//   entry = {
//     menuEntryId,                  // stable id for this entry (lets others link to it)
//     recipeRef, servings,
//     requiresPrep,                 // independent boolean: "I must prepare this"
//     servingsMade?,                // a PREPARED entry that yields leftovers cooked
//                                   // this many servings (frozen snapshot of the
//                                   // recipe's servingsMade; leftovers = made − eaten)
//     preparedBy?,                  // this entry EATS leftovers prepared by the entry
//                                   // with this menuEntryId
//     ingredients: [               // per-INGREDIENT sourcing (a meal can mix sources)
//       { ingredientRef, servings,
//         pantryStaple?: true,      // pantry staple — infinite, never bought/given back
//         fromStock?: [{ stockRef, servings }] }  // drawn from these stock rows
//     ],
//   }
//
// Buying is POOL-LEVEL, not per-entry: whole packs are bought once and the
// surplus is deposited back into the pool so later entries draw from it. The
// shortfall a single entry couldn't source from stock is emitted as `buy` ops;
// a thin applier (a later step) persists ops and the shopping list renders them.
//
// All functions here are PURE: they take pools + records and return
// { entry?, ops, ... }, mutating only the pools they are given. No DB, no I/O.
// The generator (Step 3) will be refactored to call sourceEntry so generate and
// edit share one sourcing path.

import { expiryDate, byExpiry } from './stock.js';
import { buyServingsOf, isBuyable, isAlwaysAvailable } from './ingredients.js';
import {
  componentRef,
  componentKind,
  isIngredientRecipeId,
  INGREDIENT_RECIPE_PREFIX,
} from './recipes.js';

/**
 * Build the editable stock pools from a user's stock entries.
 *
 * Unlike the generator's aggregate pool, this keeps the INDIVIDUAL stock rows
 * per ref (sorted soonest-expiry-first) so consumption can record the exact
 * `stockRef` it drew from — which an edit needs to give servings back to the
 * right row.
 *
 *   ingredients: Map<ingredientRef, { rows:[{ stockRef, servings, expiry }] }>
 *   recipes:     Map<recipeRef,     { rows:[{ stockRef, servings, expiry }] }>
 *
 * Pantry staples are NOT placed in the pool (they're infinite — handled inline).
 */
export function poolsFromStock(stock, ingredientsById) {
  const ingredients = new Map();
  const recipes = new Map();

  for (const entry of stock ?? []) {
    const servings = Number(entry.servings) || 0;
    if (servings <= 0) continue;

    if (entry.recipeRef) {
      const exp = expiryDate(entry, null);
      pushRow(recipes, entry.recipeRef, {
        stockRef: entry.itemId,
        servings,
        expiry: exp,
        // Carry the originating prepared entry so a consuming entry can link back.
        ...(entry.preparedBy ? { preparedBy: entry.preparedBy } : {}),
      });
    } else if (entry.ingredientRef) {
      const ingredient = ingredientsById?.get?.(entry.ingredientRef);
      if (isAlwaysAvailable(ingredient)) continue; // infinite (pantry/restaurant) — not pooled
      const exp = expiryDate(entry, ingredient);
      pushRow(ingredients, entry.ingredientRef, {
        stockRef: entry.itemId,
        servings,
        expiry: exp,
      });
    }
  }

  for (const bucket of [ingredients, recipes]) {
    for (const { rows } of bucket.values()) {
      rows.sort((a, b) => byExpiry(a.expiry, b.expiry));
    }
  }

  return { ingredients, recipes };
}

/** Push a row into a Map<ref, { rows:[] }> bucket, creating the bucket lazily. */
function pushRow(map, ref, row) {
  const bucket = map.get(ref);
  if (bucket) bucket.rows.push(row);
  else map.set(ref, { rows: [row] });
}

/**
 * Draw up to `need` servings of `ref` from a pool bucket, soonest-expiry-first.
 * Mutates the bucket's rows (decrementing / removing emptied rows) and returns
 * { taken, draws:[{ stockRef, servings }] } — the per-row allocation that an
 * edit can later give back.
 */
function drawFromPool(bucket, need) {
  const draws = [];
  let remaining = need;
  if (!bucket) return { taken: 0, draws };
  const rows = bucket.rows;
  let i = 0;
  while (remaining > 0 && i < rows.length) {
    const row = rows[i];
    const take = Math.min(row.servings, remaining);
    if (take > 0) {
      draws.push({ stockRef: row.stockRef, servings: take });
      row.servings -= take;
      remaining -= take;
    }
    if (row.servings <= 0) rows.splice(i, 1);
    else i += 1;
  }
  return { taken: need - remaining, draws };
}

/** Deposit (give back / add) `servings` of `ref` into a pool bucket as a row. */
function depositToPool(map, ref, row) {
  if ((Number(row.servings) || 0) <= 0) return;
  pushRow(map, ref, row);
  // Keep soonest-expiry-first so later draws still prefer perishables.
  map.get(ref).rows.sort((a, b) => byExpiry(a.expiry, b.expiry));
}

/**
 * Source ONE menu entry of `servings` servings of `recipe`, drawing each
 * ingredient component from stock soonest-expiry-first and buying whole packs
 * for any shortfall. PURE except that it mutates `pools` (so successive calls in
 * a run see the depleted/replenished stock — exactly as the generator does).
 *
 * Per ingredient component (scaled by the entry's servings):
 *   • pantry staple        -> { ingredientRef, servings, pantryStaple:true }, no ops
 *   • drawn from stock      -> fromStock:[{ stockRef, servings }] + consume ops
 *   • shortfall, buyable    -> buy ceil(shortfall / buyServings) WHOLE packs; the
 *                              surplus is deposited back into the ingredient pool
 *                              (so the next entry draws from it), emitting a `buy`
 *                              op (pool-level intent) + the drawn fromStock for the
 *                              part of the pack this entry actually eats.
 *   • shortfall, unbuyable  -> recorded in `unsourced` (caller decides) — no buy.
 *
 * If `recipe.servingsMade > servings`, preparing it yields a leftover batch: the
 * entry records `servingsMade` (its leftovers are `servingsMade − servings`) and
 * the surplus is deposited as recipe-stock so later entries can eat it (their
 * entry will carry `preparedBy` referencing THIS entry's `menuEntryId`).
 *
 * @returns { entry, ops, unsourced } where ops is the stock-op list and
 *   unsourced is [{ ingredientRef, servings }] for components that couldn't be
 *   fully sourced (no stock + not buyable).
 */
export function sourceEntry({
  recipe,
  servings = 1,
  pools,
  ingredientsById,
  // Map<recipeRef, recipe> so sub-recipe (kind:'recipe') components can be
  // resolved and sourced recursively. Optional: without it, a recipe-kind
  // component is recorded as unsourced.
  recipesById,
  date,
  genId = () => crypto.randomUUID(),
  // Minter for NEW stock-row refs (bought stock + leftover batches). Defaults to
  // genId; the generator passes a STOCK_PREFIX-aware minter so the persisted row
  // itemId matches the ref the entry points at.
  genStockId = genId,
  leftoverShelfLifeDays,
  // When true, buy EXACTLY the shortfall (no whole-pack rounding / surplus). Used
  // by the prepare/eat "sourced by magic" path so it never fabricates partial-pack
  // leftover ingredient stock. Planning (generator) leaves it false → whole packs.
  exactBuy = false,
  // Per-entry overrides of how many servings each cooked recipe KEEPS, keyed by
  // recipeRef — the "Change serving count" feature, now covering nested sub-recipe
  // cooks too. The ingredients cooked are UNCHANGED (always one full batch per cook
  // — see cookRecipeComponents); only the yield (and thus the deposited surplus)
  // differs. A missing key → the recipe's own servingsMade. Each value clamped ≥ 1;
  // may exceed the default (treated as "sourced extra from somewhere"). The
  // top-level recipe is keyed by `recipe.itemId` like any other cook.
  cookOverrides = null,
}) {
  const want = Math.max(1, Number(servings) || 1);
  const made = madeFor(recipe, cookOverrides);
  // Accumulates one row per distinct recipe this entry's sourcing cooks (top-level
  // + nested), each `{ recipeRef, default, kept, requiresPrep, batches, need }`.
  // `cooks.push` is called from the nested cook paths via ctx.
  const cooks = new Map();
  const ctx = {
    pools,
    ingredientsById,
    recipesById,
    date,
    genStockId,
    leftoverShelfLifeDays,
    exactBuy,
    cookOverrides,
    cooks,
  };

  // Cook the recipe's batches (each yields `made` servings): source every
  // component — ingredients (draw/buy) and sub-recipes (recursive
  // stock-then-cook). Returns this entry's ingredient provenance + any nested
  // cooks. Surplus (made × batches − want) is deposited as recipe-stock below.
  //
  // A NO-PREP recipe cooks as many whole batches as cover every serving the entry
  // eats (e.g. 3 serves of a 1-serve recipe = 3× its ingredients), so planning —
  // reservations, the shopping list, "In stock" — counts multi-serve entries
  // correctly. A PREP recipe stays ONE batch: its yield is managed by the cook's
  // kept-serving override ("Change serving count"), not by repeating the batch.
  const menuEntryId = genId();
  const batches = recipe.requiresPrep === false ? Math.ceil(want / made) : 1;
  const cook = cookBatches(recipe, ctx, batches);

  const entry = {
    menuEntryId,
    recipeRef: recipe.itemId,
    servings: want,
    // Derived: this entry needs prep if the recipe itself does, OR if sourcing
    // it had to COOK any sub-recipe that requires prep (e.g. "Shepherd's Pie on
    // Toast" is no-prep, but cooking the Shepherd's Pie it needs is prep).
    requiresPrep: recipe.requiresPrep !== false || cook.cookedPrep,
    ingredients: cook.ingredients,
  };
  // Provenance for recursive give-back: every recipe-stock batch this entry's
  // sourcing deposited, at any nesting level (so removal drains them all).
  if (cook.cookedBatches.length > 0) entry.cookedBatches = cook.cookedBatches;
  // Provenance for pairing: sibling-owned recipe-stock batches this entry drew a
  // sub-recipe's leftovers from (so removal gives them back AND a sibling cook's
  // regenerate/remove can pair this consumer). Only recorded going forward — menus
  // generated before this existed simply won't pair (they behave as plain
  // recipe-stock consumers, which is safe).
  if (cook.usesBatches.length > 0) entry.usesBatches = cook.usesBatches;

  const ops = [...cook.ops];
  const unsourced = [...cook.unsourced];

  // Record the TOP-LEVEL cook in the manifest (`batches` batches, each yields
  // `made`, this entry eats `want`). Nested cooks were already recorded via ctx.cooks.
  recordCook(cooks, recipe, { kept: made, need: want, batches });

  // Surplus servings beyond what this entry eats become a leftover batch of the
  // TOP-LEVEL recipe, linked to this entry so removal can drain it.
  const surplus = made * batches - want;
  if (surplus > 0) {
    // Total yield across the batches (so leftoverServingsOf = surplus).
    entry.servingsMade = made * batches;
    const days = leftoverShelfLifeDays ?? DEFAULT_LEFTOVER_DAYS;
    const stockId = genStockId();
    ops.push({
      type: 'addRecipeStock',
      stockRef: stockId,
      recipeRef: recipe.itemId,
      servings: surplus,
      obtainedDate: date,
      shelfLifeDays: days,
      preparedBy: menuEntryId,
    });
    depositToPool(pools.recipes, recipe.itemId, {
      stockRef: stockId,
      servings: surplus,
      expiry: addDays(date, days),
      preparedBy: menuEntryId,
    });
    // This top-level surplus batch is also owned by the entry (drained on remove).
    (entry.cookedBatches ??= []).push({
      recipeRef: recipe.itemId,
      stockRef: stockId,
      servings: surplus,
      preparedBy: menuEntryId,
    });
  }

  // Attach the cook manifest + any surviving overrides so the UI can show/edit
  // every cook's kept-serving count. `giveAwayWarnings` lists cooks kept BELOW
  // what the plan committed to that recipe (sourced normally — stock stays
  // correct — but surfaced as "warn but allow").
  const cookList = [...cooks.values()];
  if (cookList.length > 0) entry.cooks = cookList;
  const warnings = cookList
    .filter((c) => c.kept < c.need)
    .map((c) => ({
      recipeRef: c.recipeRef,
      kept: c.kept,
      needed: c.need,
    }));
  if (warnings.length > 0) entry.giveAwayWarnings = warnings;

  return { entry, ops, unsourced };
}

/** Kept-serving yield for ONE batch of `recipe`, honouring a per-recipe override
 * (keyed by recipeRef) from the entry's `cookOverrides`. Clamped ≥ 1; may exceed
 * the recipe default ("sourced extra from somewhere"). PURE. */
function madeFor(recipe, cookOverrides) {
  const ov = cookOverrides?.[recipe.itemId];
  if (ov !== null && ov !== undefined && Number(ov) >= 1) {
    return Math.max(1, Math.floor(Number(ov)));
  }
  return Math.max(1, Number(recipe.servingsMade) || 1);
}

/** Record (or accumulate) a cook of `recipe` in the manifest Map (keyed by
 * recipeRef). `need` is how many servings of this recipe the plan asked for at
 * this cook; `kept` is the per-batch yield used. Multiple cooks of the same
 * recipe sum their batch count and need. PURE. */
function recordCook(cooks, recipe, { kept, need, batches = 1 }) {
  const ref = recipe.itemId;
  const add = Math.max(1, Number(batches) || 1);
  const existing = cooks.get(ref);
  if (existing) {
    existing.batches += add;
    existing.need += Math.max(0, Number(need) || 0);
    return;
  }
  cooks.set(ref, {
    recipeRef: ref,
    name: recipe.name ?? null,
    default: Math.max(1, Number(recipe.servingsMade) || 1),
    kept: Math.max(1, Math.floor(Number(kept) || 1)),
    requiresPrep: recipe.requiresPrep !== false,
    batches: add,
    need: Math.max(0, Number(need) || 0),
  });
}

/** Source `batches` whole batches of `recipe` (see cookRecipeComponents),
 * concatenating their results. PURE except it mutates ctx.pools. */
function cookBatches(recipe, ctx, batches) {
  const acc = {
    ops: [],
    ingredients: [],
    unsourced: [],
    cookedPrep: false,
    cookedBatches: [],
    usesBatches: [],
  };
  for (let i = 0; i < batches; i++) {
    const cook = cookRecipeComponents(recipe, ctx);
    acc.ops.push(...cook.ops);
    acc.ingredients.push(...cook.ingredients);
    acc.unsourced.push(...cook.unsourced);
    acc.cookedBatches.push(...cook.cookedBatches);
    acc.usesBatches.push(...cook.usesBatches);
    acc.cookedPrep = acc.cookedPrep || cook.cookedPrep;
  }
  return acc;
}

/**
 * Source ONE batch of `recipe`'s components (the amounts prepare the WHOLE batch,
 * which yields servingsMade — components are per-batch, not per-serving). PURE
 * except it mutates ctx.pools.
 *
 * Ingredient components: draw stock soonest-expiry-first, buy whole packs for the
 * shortfall (buyable) or record `unsourced`. Sub-recipe components: recurse via
 * sourceRecipeServings (draw sub recipe-stock, else cook nested batches).
 *
 * @returns {{ ops, ingredients, unsourced, cookedPrep, cookedBatches }}
 *   ingredients   — per-ingredient provenance rows for the entry (flattened to
 *                   include ingredients pulled up from cooked sub-recipes)
 *   cookedPrep    — true if any sub-recipe that had to be COOKED requires prep
 *   cookedBatches — every recipe-stock batch deposited while cooking sub-recipes
 */
function cookRecipeComponents(recipe, ctx) {
  const { ingredientsById, recipesById } = ctx;
  const ops = [];
  const unsourced = [];
  const ingredients = [];
  const cookedBatches = [];
  const usesBatches = [];
  let cookedPrep = false;

  for (const c of recipe.components ?? []) {
    const ref = componentRef(c);
    const need = Number(c.servings) || 0;

    if (componentKind(c) === 'recipe') {
      // Sub-recipe component: stock-then-cook the required servings.
      const sub = recipesById?.get?.(ref);
      if (!sub) {
        // Unknown sub-recipe (not loaded) — can't source it.
        if (need > 0) unsourced.push({ recipeRef: ref, servings: need });
        continue;
      }
      const sourced = sourceRecipeServings(sub, need, ctx);
      ops.push(...sourced.ops);
      unsourced.push(...sourced.unsourced);
      // Flatten the sub-recipe's ingredient draws into this entry's provenance so
      // give-back returns them (a cooked sub-recipe has no standalone entry).
      ingredients.push(...sourced.ingredients);
      cookedBatches.push(...sourced.cookedBatches);
      usesBatches.push(...sourced.usesBatches);
      cookedPrep = cookedPrep || sourced.cookedPrep;
      continue;
    }

    // Ingredient component. Always-available ingredients (pantry staples AND
    // restaurant items) are treated as infinite: marked pantryStaple so no ops are
    // emitted and give-back skips them (the marker means "never bought/given back").
    const ingredient = ingredientsById?.get?.(ref);
    if (isAlwaysAvailable(ingredient)) {
      ingredients.push({ ingredientRef: ref, servings: need, pantryStaple: true });
      continue;
    }
    if (need <= 0) {
      ingredients.push({ ingredientRef: ref, servings: 0 });
      continue;
    }
    const sourced = sourceIngredient(ref, need, ingredient, ctx);
    ops.push(...sourced.ops);
    if (sourced.unsourced) unsourced.push(sourced.unsourced);
    ingredients.push(sourced.ingredient);
  }

  return { ops, ingredients, unsourced, cookedPrep, cookedBatches, usesBatches };
}

/**
 * Source `need` servings of ONE ingredient: draw stock soonest-expiry-first, buy
 * whole packs for the shortfall (buyable) or return an `unsourced` marker. PURE
 * except it mutates ctx.pools. Returns { ops, ingredient, unsourced? }.
 */
function sourceIngredient(ref, need, ingredient, ctx) {
  const { pools, date, genStockId } = ctx;
  const ops = [];

  // 1) Draw what we can from existing stock, soonest-expiry-first.
  const { taken, draws } = drawFromPool(pools.ingredients.get(ref), need);
  for (const d of draws) {
    ops.push({
      type: 'consumeIngredient',
      stockRef: d.stockRef,
      ingredientRef: ref,
      servings: d.servings,
    });
  }

  const shortfall = need - taken;
  let unsourced = null;

  // 2) Buy for any shortfall, if the ingredient is buyable.
  if (shortfall > 0) {
    if (isBuyable(ingredient)) {
      const per = buyServingsOf(ingredient); // > 0 (isBuyable guaranteed)
      // Planning (generator / shopping list) buys WHOLE packs. But the prepare/eat
      // "sourced by magic" path (ctx.exactBuy) buys EXACTLY the shortfall: the user
      // is asserting they already have the ingredient on hand, so materialising a
      // partial-pack surplus would leave fake leftover ingredient stock. Buy just
      // what's needed (packs is a fractional record of the servings bought).
      const bought = ctx.exactBuy ? shortfall : Math.ceil(shortfall / per) * per;
      const packs = ctx.exactBuy ? shortfall / per : Math.ceil(shortfall / per);
      ops.push({
        type: 'buy',
        ingredientRef: ref,
        packs,
        buyServings: per,
        buyUnit: ingredient?.buyUnit ?? null,
      });
      // Deposit the WHOLE pack, then draw the shortfall so fromStock points at the
      // new row and the surplus stays pooled for later entries.
      const newRowId = genStockId();
      const exp = ingredientExpiryFor(ingredient, date);
      ops.push({
        type: 'addIngredientStock',
        stockRef: newRowId,
        ingredientRef: ref,
        servings: bought,
        obtainedDate: date,
      });
      depositToPool(pools.ingredients, ref, { stockRef: newRowId, servings: bought, expiry: exp });
      const after = drawFromPool(pools.ingredients.get(ref), shortfall);
      for (const d of after.draws) {
        ops.push({
          type: 'consumeIngredient',
          stockRef: d.stockRef,
          ingredientRef: ref,
          servings: d.servings,
        });
        draws.push(d);
      }
    } else {
      unsourced = { ingredientRef: ref, servings: shortfall };
    }
  }

  const fromStock = mergeDraws(draws);
  const ingredientRow = fromStock.length
    ? { ingredientRef: ref, servings: need, fromStock }
    : { ingredientRef: ref, servings: need };
  return { ops, ingredient: ingredientRow, unsourced };
}

/**
 * Source `need` servings of a sub-recipe: draw its existing recipe-stock
 * (leftovers) soonest-expiry-first, then COOK whole batches for the shortfall
 * (recursing into its own components). Any surplus from a cooked batch is
 * deposited as recipe-stock (nested leftovers). PURE except it mutates ctx.pools.
 *
 * @returns {{ ops, ingredients, unsourced, cookedPrep, cookedBatches, drawn }}
 *   ingredients   — ingredient provenance from the batches this call cooked
 *                   (flattened; a cooked sub-recipe has no standalone entry)
 *   cookedPrep    — true if this recipe (or a descendant) was cooked AND needs prep
 *   cookedBatches — recipe-stock batches deposited (this recipe's surplus + nested)
 *   drawn         — servings satisfied from existing recipe-stock (no cook)
 */
function sourceRecipeServings(recipe, need, ctx) {
  const { pools, date, genStockId, leftoverShelfLifeDays, cookOverrides, cooks } = ctx;
  const want = Math.max(0, Number(need) || 0);
  const ops = [];
  const ingredients = [];
  const unsourced = [];
  const cookedBatches = [];
  // Batches OWNED BY ANOTHER (sibling) entry that this sourcing drew leftovers
  // from — recorded so removal/regeneration can pair the consumer with that cook
  // (see entry.usesBatches). Distinct from cookedBatches (batches this sourcing
  // deposited itself).
  const usesBatches = [];
  let cookedPrep = false;
  if (want <= 0)
    return { ops, ingredients, unsourced, cookedPrep, cookedBatches, usesBatches, drawn: 0 };

  // 1) Draw from existing recipe-stock first (leftovers), soonest-expiry-first.
  // Capture each drawn row's owning batch (`preparedBy`) BEFORE the draw so we can
  // link this consumer to the sibling cook that prepared it.
  const bucket = pools.recipes.get(recipe.itemId);
  const preparedByOf = new Map((bucket?.rows ?? []).map((r) => [r.stockRef, r.preparedBy]));
  const { taken: drawn, draws } = drawFromPool(bucket, want);
  for (const d of draws) {
    ops.push({
      type: 'consumeRecipeStock',
      stockRef: d.stockRef,
      recipeRef: recipe.itemId,
      servings: d.servings,
    });
    const preparedBy = preparedByOf.get(d.stockRef);
    if (preparedBy) {
      usesBatches.push({
        recipeRef: recipe.itemId,
        stockRef: d.stockRef,
        servings: d.servings,
        preparedBy,
      });
    }
  }

  let shortfall = want - drawn;
  // Nested yield honours a per-recipe override (keyed by recipeRef) exactly like
  // the top level — the batch's ingredients are unchanged; only the kept yield
  // (and thus deposited surplus) differs.
  const made = madeFor(recipe, cookOverrides);

  // Record this nested cook in the manifest when it actually cooks a batch
  // (shortfall > 0). `need` is what the parent asked for of this recipe; `batches`
  // is how many whole batches cover the shortfall at this yield.
  if (shortfall > 0 && cooks) {
    const batchCount = Math.ceil(shortfall / made);
    recordCook(cooks, recipe, { kept: made, need: want, batches: batchCount });
  }

  // 2) Cook whole batches until the shortfall is covered. Each batch sources the
  // recipe's components once (recursively) and yields `made` servings.
  while (shortfall > 0) {
    const batchId = genStockId();
    const cook = cookRecipeComponents(recipe, ctx);
    ops.push(...cook.ops);
    unsourced.push(...cook.unsourced);
    ingredients.push(...cook.ingredients);
    cookedBatches.push(...cook.cookedBatches);
    // Cooking THIS recipe is a prep action if it requires prep; also bubble up
    // any nested cooked-prep.
    cookedPrep = cookedPrep || recipe.requiresPrep !== false || cook.cookedPrep;

    const useFromBatch = Math.min(shortfall, made);
    const surplus = made - useFromBatch;
    shortfall -= useFromBatch;

    // Deposit this batch's surplus as recipe-stock (nested leftovers).
    if (surplus > 0) {
      const days = leftoverShelfLifeDays ?? DEFAULT_LEFTOVER_DAYS;
      ops.push({
        type: 'addRecipeStock',
        stockRef: batchId,
        recipeRef: recipe.itemId,
        servings: surplus,
        obtainedDate: date,
        shelfLifeDays: days,
        preparedBy: batchId,
      });
      depositToPool(pools.recipes, recipe.itemId, {
        stockRef: batchId,
        servings: surplus,
        expiry: addDays(date, days),
        preparedBy: batchId,
      });
      cookedBatches.push({
        recipeRef: recipe.itemId,
        stockRef: batchId,
        servings: surplus,
        preparedBy: batchId,
      });
    }
  }

  return { ops, ingredients, unsourced, cookedPrep, cookedBatches, usesBatches, drawn };
}

/**
 * Place a no-prep entry that draws `servings` of an already-prepared recipe from
 * recipe-stock (leftovers), soonest-expiry-first. PURE — mutates only the given
 * pools. Returns `{ entry: null, ops: [] }` when no recipe-stock is available so
 * the caller can fall back to sourceEntry (make/buy).
 *
 * The returned entry carries `fromRecipeStock` (the rows drawn) and `preparedBy`
 * (the menuEntryId of the entry that originally prepared the batch, carried on
 * the pooled stock row), so a later edit can give the servings back via
 * removeEntry. `requiresPrep` is false — the batch was already prepared.
 */
export function consumeRecipeStock({
  recipe,
  servings = 1,
  pools,
  genId = () => crypto.randomUUID(),
}) {
  const want = Math.max(1, Number(servings) || 1);
  const bucket = pools.recipes.get(recipe.itemId);
  if (!bucket || bucket.rows.length === 0) return { entry: null, ops: [] };

  // Capture the originating batch's preparedBy before drawing (front row first).
  const preparedBy = bucket.rows[0].preparedBy;
  const { taken, draws } = drawFromPool(bucket, want);
  if (taken <= 0) return { entry: null, ops: [] };

  const ops = draws.map((d) => ({
    type: 'consumeRecipeStock',
    stockRef: d.stockRef,
    recipeRef: recipe.itemId,
    servings: d.servings,
  }));

  const entry = {
    menuEntryId: genId(),
    recipeRef: recipe.itemId,
    servings: taken,
    requiresPrep: false,
    fromRecipeStock: mergeDraws(draws),
  };
  if (preparedBy) entry.preparedBy = preparedBy;
  return { entry, ops };
}

/**
 * Real-time lifecycle. In the real-time stock model, consumption no longer
 * happens at menu save; it happens when the user PREPARES a batch (cooks
 * it) and EATS a serving. These pure fns emit the stock ops for those two
 * actions; the caller (menu page) persists them via `applyStockOps` and flips the
 * entry's `prepared`/`eaten` markers.
 */

/** True once a cook entry's batch has been prepared (cooked). */
export function isPrepared(entry) {
  return !!entry?.prepared;
}

/**
 * Lifecycle status of a menu entry:
 *   'eaten'    — no servings remain (fully eaten)
 *   'prepared' — batch cooked (recipe-stock exists) but not fully eaten
 *   'planned'  — not yet prepared/eaten (reserves stock only)
 * Leftover entries (they eat an already-prepared batch) are 'planned' until eaten
 * — they have nothing to prepare themselves.
 */
export function entryStatus(entry) {
  if (!entry) return 'planned';
  if (remainingServingsOf(entry) <= 0 && (Number(entry.eaten) || 0) > 0) return 'eaten';
  if (isPrepared(entry)) return 'prepared';
  return 'planned';
}

/**
 * Does this menu entry still "need shopping" — i.e. would sourcing it at its slot
 * fall short of what's genuinely on hand? This is a cheap membership check against
 * the id set produced by `shoppingNeededEntryIds` (which does the chronological,
 * stock-aware sourcing replay). The structural guards below are kept so a caller
 * can call this directly on an entry without first filtering.
 *
 * Excluded (they never "need shopping"):
 *   • quick-energy entries (no recipe)
 *   • prepared cooks and leftover entries — their food already exists as
 *     recipe-stock, so buying ingredients is moot.
 *   • fully-eaten entries.
 *
 * @param {Set<string>} neededIds  menuEntryIds flagged by shoppingNeededEntryIds.
 */
export function entryNeedsShopping(entry, neededIds) {
  if (!entry || !neededIds || neededIds.size === 0) return false;
  if (entry.kind === 'quick') return false;
  if (isPrepared(entry)) return false;
  if ((entry.fromRecipeStock ?? []).length > 0) return false; // leftover entry
  if (remainingServingsOf(entry) <= 0) return false;
  return neededIds.has(entry.menuEntryId);
}

/**
 * Chronological, stock-aware "needs shopping" classifier. Walks every entry across
 * all menus in strict (date, slot) order, drawing down a pool seeded ONLY from
 * current on-hand stock (plus recipe-stock leftovers that EARLIER entries in the
 * plan cook). An entry is flagged when sourcing it at its slot cannot be fully
 * covered by that pool — i.e. it has to BUY or leaves something `unsourced`.
 *
 * Semantics (agreed): "every short-of-raw-stock day". A whole-pack buy on an early
 * day does NOT suppress the badge on later days — bought stock is not "on hand".
 * So 2 celery on hand + 1/day for 3 days flags only the 3rd day. We achieve this
 * by STRIPPING each entry's just-bought ingredient surplus back out of the pool
 * after sourcing it; recipe-stock leftovers that cooks deposit are KEPT (genuinely
 * produced by the plan), so a cook's later leftover-eaters are correctly covered.
 *
 * Handles virtual snack-ingredient recipes (they source through the same path),
 * sub-recipes (recursive), and per-recipe `cookOverrides` kept-yield overrides.
 * PURE: builds its own pools; never mutates the caller's data.
 *
 * @returns {Set<string>} the menuEntryIds that need shopping.
 */
export function shoppingNeededEntryIds(
  menus,
  stock,
  ingredientsById,
  recipesById,
  { slotRankOf = () => 0 } = {},
) {
  const flagged = new Set();
  const pools = poolsFromStock(stock, ingredientsById);

  // sortedMenuDays resolves each day's `date` (from `date` or the `menu#<date>`
  // itemId) so this works for both the edit session's dated days and the saved
  // view-mode `Map<date, menuRow>` whose rows carry no `date` field.
  const days = sortedMenuDays(menus);

  let seq = 0;
  const genId = () => `nsid${seq++}`;

  for (const day of days) {
    for (const entry of orderedDayEntries(day, slotRankOf)) {
      // Mirror entryNeedsShopping's structural exclusions.
      if (entry.kind === 'quick') continue;
      if (isPrepared(entry)) continue; // batch already cooked — ingredients gone
      if ((entry.fromRecipeStock ?? []).length > 0) {
        // Leftover entry: eats an existing batch. Draw it so later eaters see the
        // depletion, but it never needs shopping itself.
        const r = recipesById?.get?.(entry.recipeRef);
        if (r)
          consumeRecipeStock({ recipe: r, servings: remainingServingsOf(entry), pools, genId });
        continue;
      }
      const remaining = remainingServingsOf(entry);
      if (remaining <= 0) continue;
      const recipe = recipesById?.get?.(entry.recipeRef);
      if (!recipe) continue; // unresolved — can't classify

      // Prefer existing recipe-stock (leftovers) first, exactly like the menu page
      // / reservationsFromMenus; only the shortfall falls through to a full source.
      const drawn = consumeRecipeStock({ recipe, servings: remaining, pools, genId });
      const fromLeftovers = drawn.entry ? drawn.entry.servings : 0;
      const short = remaining - fromLeftovers;
      if (short <= 0) continue; // fully covered by on-hand leftovers → no shopping

      const res = sourceEntry({
        recipe,
        servings: short,
        pools,
        ingredientsById,
        recipesById,
        date: '1970-01-01',
        genId,
        cookOverrides: entry.cookOverrides ?? null,
      });

      // Short of raw stock iff sourcing had to buy or left something unsourced.
      const bought = res.ops.filter((o) => o.type === 'addIngredientStock');
      if (bought.length > 0 || res.unsourced.length > 0) flagged.add(entry.menuEntryId);

      // "Every short day": strip the surplus of each just-bought ingredient pack
      // back out of the pool so it can't cover LATER days (bought ≠ on hand). The
      // part this entry ate is already drawn; we remove whatever of that row
      // remains. Recipe-stock leftovers (addRecipeStock) are intentionally KEPT.
      for (const add of bought) {
        removeStockRefFromPool(pools.ingredients, add.ingredientRef, add.stockRef);
      }
    }
  }

  return flagged;
}

/** Remove ALL remaining servings of a specific stockRef from an ingredient pool
 * bucket (used to un-replenish a just-bought pack's surplus). */
function removeStockRefFromPool(map, ref, stockRef) {
  const bucket = map.get(ref);
  if (!bucket) return;
  bucket.rows = bucket.rows.filter((r) => r.stockRef !== stockRef);
  if (bucket.rows.length === 0) map.delete(ref);
}

/**
 * PREPARE a cook entry's WHOLE batch: source every component (draw stock
 * soonest-expiry-first, buy shortfall, recursively cook sub-recipes) and deposit
 * ALL `servingsMade` servings as recipe-stock linked to this entry
 * (`preparedBy = entry.menuEntryId`). The entry then EATS from that recipe-stock
 * via `eatServe` — even its own serving — so prepare and eat are cleanly
 * separated. PURE except it mutates the given pools.
 *
 * Does NOT mark the entry; the caller sets `entry.prepared = true`,
 * `entry.servingsMade`, and `entry.cookedBatches` from the return value.
 *
 * @returns {{ ops, unsourced, servingsMade, cookedBatches }}
 */
export function prepareEntry({
  entry,
  recipe,
  pools,
  ingredientsById,
  recipesById,
  date,
  genId = () => crypto.randomUUID(),
  genStockId = genId,
  leftoverShelfLifeDays,
}) {
  // Batch yield honours this entry's own `cookOverrides` ("Change serving count"):
  // the deposited recipe-stock may be more or fewer servings than the recipe's
  // default, but the ingredients cooked below are UNCHANGED (one batch). Nested
  // sub-recipe cooks honour their own override via ctx.cookOverrides.
  const made = madeServingsOf(entry, recipe);
  // exactBuy: preparing asserts the ingredients are on hand, so buy only the
  // shortfall (no partial-pack surplus → no fake leftover ingredient stock).
  const ctx = {
    pools,
    ingredientsById,
    recipesById,
    date,
    genStockId,
    leftoverShelfLifeDays,
    exactBuy: true,
    cookOverrides: entry.cookOverrides ?? null,
  };

  // Cook one batch: source components (this yields `made` servings' worth).
  const cook = cookRecipeComponents(recipe, ctx);
  const ops = [...cook.ops];
  const unsourced = [...cook.unsourced];
  const cookedBatches = [...cook.cookedBatches];

  // Deposit the ENTIRE batch as recipe-stock owned by this entry. Eating (incl.
  // this entry's own serving) later draws from here.
  const days = leftoverShelfLifeDays ?? DEFAULT_LEFTOVER_DAYS;
  const stockId = genStockId();
  const preparedBy = entry.menuEntryId;
  ops.push({
    type: 'addRecipeStock',
    stockRef: stockId,
    recipeRef: recipe.itemId,
    servings: made,
    obtainedDate: date,
    shelfLifeDays: days,
    preparedBy,
  });
  depositToPool(pools.recipes, recipe.itemId, {
    stockRef: stockId,
    servings: made,
    expiry: addDays(date, days),
    preparedBy,
  });
  cookedBatches.push({ recipeRef: recipe.itemId, stockRef: stockId, servings: made, preparedBy });

  return { ops, unsourced, servingsMade: made, cookedBatches };
}

/**
 * EAT one serving of an entry. Draws from stock:
 *   - a PREPARED cook entry or a LEFTOVER entry → one serving of recipe-stock
 *     (this recipe's leftovers, soonest-expiry-first);
 *   - a no-prep entry → consume its ingredients directly for one serving
 *     (prepare+eat fused — no batch was cooked).
 * PURE except it mutates the given pools. Does NOT mark the entry; the caller
 * increments `entry.eaten`. Returns `{ ops, unsourced }`.
 *
 * `servings` lets a multi-serving entry eat its whole count at once (default 1).
 */
export function eatServe({
  entry,
  recipe,
  pools,
  ingredientsById,
  recipesById,
  date = '1970-01-01',
  servings = 1,
  genId = () => crypto.randomUUID(),
  genStockId = genId,
}) {
  const want = Math.max(1, Number(servings) || 1);

  // Prepared batch or leftover entry → eat from recipe-stock.
  const isBatchEater = isPrepared(entry) || !!entry.preparedBy || !!entry.fromRecipeStock;
  if (isBatchEater) {
    const drawn = consumeRecipeStock({ recipe, servings: want, pools, genId });
    if (drawn.entry) {
      // Only the ops matter here (draws from recipe-stock); the caller owns the
      // menu entry and just flips `eaten`.
      return { ops: drawn.ops, unsourced: [] };
    }
    // Nothing left to draw (shouldn't happen if prepared) — fall through to a
    // direct ingredient consume as a safety net.
  }

  // No-prep entry: consume ingredients directly for one serving (fused prep+eat).
  // exactBuy: eating asserts the ingredients are on hand — buy only the shortfall.
  const res = sourceEntry({
    recipe,
    servings: want,
    pools,
    ingredientsById,
    recipesById,
    date,
    genId,
    genStockId,
    exactBuy: true,
    cookOverrides: entry.cookOverrides ?? null,
  });
  // sourceEntry emits any surplus leftover batch for a made>want recipe; for a
  // no-prep single-serve eat that's a no-op. Return only its consume/buy ops.
  return { ops: res.ops, unsourced: res.unsourced };
}

/**
 * How many servings ONE prepared batch of this entry yields. A per-recipe
 * `cookOverrides` entry for the entry's own recipeRef (the "Change serving
 * count" feature) wins; otherwise the recipe's CURRENT `servingsMade`, which is
 * what planning, reservations and the shopping list all source with. The
 * entry's own `servingsMade` is a planning-time snapshot that goes stale when
 * the recipe's yield is edited later, so it's only the fallback when no recipe
 * is given. Always ≥ 1. PURE.
 */
export function madeServingsOf(entry, recipe = null) {
  const override = topLevelOverride(entry);
  if (override !== null) return override;
  const made = Number(recipe?.servingsMade) || Number(entry?.servingsMade) || 0;
  return Math.max(1, made || 1);
}

/**
 * Enumerate every recipe this entry would COOK when prepared — the top-level
 * recipe plus every sub-recipe cooked at any depth — as a manifest the UI can show
 * and edit. Returns `entry.cooks` rows `{ recipeRef, name, default, kept,
 * requiresPrep, batches, need }` (see recordCook).
 *
 * It drives this by running a sourcing pass against EMPTY pools (nothing on hand),
 * so every recipe in the tree is forced to cook and thus recorded — independent of
 * the user's real stock. `cookOverrides` (the entry's current kept-yield overrides)
 * are honoured so `kept` reflects any in-progress edits. PURE: builds its own pools
 * and a throwaway entry; never mutates the caller's data.
 *
 * @param {object} entry        the menu entry (needs recipeRef, servings, cookOverrides?)
 * @param {object} recipe       the resolved top-level recipe
 * @param {Map} ingredientsById
 * @param {Map} recipesById
 * @returns {Array} the cook manifest (possibly empty)
 */
export function cookManifestFor(entry, recipe, ingredientsById, recipesById) {
  if (!recipe) return [];
  let seq = 0;
  const genId = () => `cm${seq++}`;
  const { entry: sourced } = sourceEntry({
    recipe,
    servings: Math.max(1, remainingServingsOf(entry) || Number(entry?.servings) || 1),
    pools: { ingredients: new Map(), recipes: new Map() },
    ingredientsById,
    recipesById,
    date: '1970-01-01',
    genId,
    cookOverrides: entry?.cookOverrides ?? null,
  });
  return sourced.cooks ?? [];
}

/** The entry's own (top-level recipe) kept-serving override, or null when none.
 * Reads the unified `cookOverrides` map keyed by the entry's own recipeRef. ≥ 1
 * when present. PURE. */
function topLevelOverride(entry) {
  const ov = entry?.cookOverrides?.[entry?.recipeRef];
  if (ov !== null && ov !== undefined && Number(ov) >= 1) {
    return Math.max(1, Math.floor(Number(ov)));
  }
  return null;
}

/**
 * Leftover servings a PREPARED entry yields: what the TOP-LEVEL recipe cooks (its
 * kept batch yield — honouring a `cookOverrides` entry for its own recipeRef)
 * minus what it eats itself. 0 when it isn't a prepared batch (no `servingsMade`
 * and no override).
 */
export function leftoverServingsOf(entry) {
  const override = topLevelOverride(entry);
  const made = override !== null ? override : Number(entry?.servingsMade) || 0;
  if (made <= 0) return 0;
  return Math.max(0, made - (Number(entry.servings) || 0));
}

/** Servings of an entry NOT yet eaten = servings − eaten (clamped to [0, servings]). */
export function remainingServingsOf(entry) {
  const servings = Math.max(0, Number(entry?.servings) || 0);
  const eaten = Math.max(0, Number(entry?.eaten) || 0);
  return Math.max(0, servings - eaten);
}

/**
 * Find the leftover menu entries across a run that consumed a given prepared
 * entry's batch — i.e. entries whose `preparedBy` equals `menuEntryId`. Returns
 * `[{ date, ...entry }]` in date order (so promote picks the soonest), ready to
 * pass to removeEntry/resolvePreparedEntryRemoval. PURE — reads only.
 *
 * @param menus Map<date, { entries:[…] }> (or any iterable of [date, menu])
 */
export function leftoverEntriesFor(menus, menuEntryId, slotRankOf = () => 0) {
  if (!menuEntryId) return [];
  const found = [];
  for (const [date, menu] of menus) {
    for (const e of menu?.entries ?? []) {
      if (e.preparedBy === menuEntryId) found.push({ date, ...e });
    }
  }
  found.sort((a, b) => compareEntryOrder(a, b, slotRankOf));
  return found;
}

/**
 * Find the RECURSIVE consumers of a cook's batches: entries for a DIFFERENT
 * recipe that drew this cook's recipe-stock while sourcing it as a SUB-recipe
 * (recorded in `entry.usesBatches[].preparedBy`). Complements `leftoverEntriesFor`
 * (which finds same-recipe leftover entries via `preparedBy`).
 *
 * Matching is by the cook's batch owners (`cook.cookedBatches[].preparedBy`), so
 * it only pairs LIVE menu entries — if the cook's day has been archived (its menu
 * entry gone), there's nothing to pair and its leftovers are just free
 * recipe-stock the consumer keeps using (archival-safe by construction).
 *
 * @returns `[{ date, ...entry }]` in menu order, excluding the cook itself.
 */
export function recursiveConsumersOf(menus, cook, slotRankOf = () => 0) {
  const owners = new Set((cook?.cookedBatches ?? []).map((b) => b.preparedBy).filter(Boolean));
  if (owners.size === 0) return [];
  const found = [];
  for (const [date, menu] of menus) {
    for (const e of menu?.entries ?? []) {
      if (e.menuEntryId === cook?.menuEntryId) continue; // never the cook itself
      const uses = e.usesBatches ?? [];
      if (uses.some((b) => owners.has(b.preparedBy))) found.push({ date, ...e });
    }
  }
  found.sort((a, b) => compareEntryOrder(a, b, slotRankOf));
  return found;
}

/**
 * UNIFIED discovery of every entry that depends on a cook's batch, regardless of
 * HOW it depends. Two kinds of dependent, treated as the same mechanism:
 *   - `link: 'leftover'`  — a SAME-recipe entry eating the batch (preparedBy).
 *   - `link: 'subrecipe'` — a DIFFERENT recipe that drew the batch as a sub-recipe
 *                            component (usesBatches[].preparedBy).
 *
 * The cook's own menu entry AND its own batches are the match key: leftovers match
 * `preparedBy === cook.menuEntryId`; sub-recipe consumers match any of the cook's
 * `cookedBatches[].preparedBy` (which, for a top-level batch, IS the cook's
 * menuEntryId, but nested batches carry their own owners). Each result carries a
 * `link` tag so the caller can offer kind-appropriate choices. Live entries only,
 * so it degrades gracefully once a cook's day is archived (archival-safe).
 *
 * @returns `[{ date, link, ...entry }]` in menu order, excluding the cook itself.
 */
export function dependentsOfCook(menus, cook, slotRankOf = () => 0) {
  if (!cook?.menuEntryId) return [];
  const owners = new Set((cook.cookedBatches ?? []).map((b) => b.preparedBy).filter(Boolean));
  owners.add(cook.menuEntryId); // top-level batch owner is the cook itself
  const found = [];
  for (const [date, menu] of menus) {
    for (const e of menu?.entries ?? []) {
      if (e.menuEntryId === cook.menuEntryId) continue; // never the cook itself
      if (e.preparedBy === cook.menuEntryId) {
        found.push({ date, link: 'leftover', ...e });
        continue;
      }
      const uses = e.usesBatches ?? [];
      if (uses.some((b) => owners.has(b.preparedBy))) {
        found.push({ date, link: 'subrecipe', ...e });
      }
    }
  }
  found.sort((a, b) => compareEntryOrder(a, b, slotRankOf));
  return found;
}

/**
 * Plan a change to a cook entry's batch yield ("Change serving count").
 *
 * The yield (`newMade`, ≥ 1) sets how many servings ONE prepared batch produces;
 * the ingredients cooked never change. A batch currently feeds `committed`
 * servings = the cook's own planned servings + every dependent's (leftover eaters
 * and sub-recipe consumers). If `newMade >= committed` the change is free. If
 * `newMade < committed` we must FREE `committed − newMade` servings off the menus,
 * LATEST-first, reducing each entry's serving count and removing it only when it
 * reaches zero (dependents trimmed before the cook's own serving — the cook is the
 * last resort so its own meal survives if at all possible).
 *
 * PURE — reads only; returns a plan the caller applies (reduce/remove entries then
 * stamp the top-level recipe's kept yield into `cookOverrides[recipeRef] = newMade`).
 * The caller prompts the user before applying when `trims` is non-empty.
 *
 * @returns {{
 *   newMade: number,
 *   committed: number,
 *   freeNeeded: number,                       // servings to remove (0 ⇒ no prompt)
 *   trims: Array<{ menuEntryId, date, isCook, reduceBy, remove, name? }>,
 * }}
 */
export function planServingsOverride({
  cook,
  cookDate,
  dependents = [],
  newMade,
  recipesById = null,
  recipeNameOf = null,
}) {
  const made = Math.max(1, Math.floor(Number(newMade) || 1));
  const cookServings = remainingServingsOf(cook);
  const depCommitted = dependents.reduce((s, d) => s + remainingServingsOf(d), 0);
  const committed = cookServings + depCommitted;
  const nameOf = (entry) =>
    recipeNameOf?.(entry) ?? recipesById?.get?.(entry.recipeRef)?.name ?? entry.recipeRef;

  const trims = [];
  const freeNeeded = Math.max(0, committed - made);
  if (freeNeeded === 0) {
    return { newMade: made, committed, freeNeeded: 0, trims: [] };
  }

  // Free servings LATEST-first from dependents, then (last resort) the cook.
  // dependents arrive in menu order (ascending); walk them from the end.
  let remaining = freeNeeded;
  const ordered = [...dependents].sort((a, b) => compareEntryOrder(a, b));
  for (let i = ordered.length - 1; i >= 0 && remaining > 0; i -= 1) {
    const dep = ordered[i];
    const have = remainingServingsOf(dep);
    if (have <= 0) continue;
    const reduceBy = Math.min(have, remaining);
    trims.push({
      menuEntryId: dep.menuEntryId,
      date: dep.date ?? null,
      isCook: false,
      reduceBy,
      remove: reduceBy >= have,
      name: nameOf(dep),
    });
    remaining -= reduceBy;
  }
  // Still short → trim the cook's own serving count (keep ≥ 0; if it hits 0 the
  // cook meal itself is removed). This only happens when newMade < cook.servings.
  if (remaining > 0 && cookServings > 0) {
    const reduceBy = Math.min(cookServings, remaining);
    trims.push({
      menuEntryId: cook.menuEntryId,
      date: cookDate ?? null,
      isCook: true,
      reduceBy,
      remove: reduceBy >= cookServings,
      name: nameOf(cook),
    });
    // (remaining is now 0; no further trimming is possible — the cook is the last
    // resort. Not reassigned since nothing reads it afterwards.)
  }

  return { newMade: made, committed, freeNeeded, trims };
}

/**
 * Temporal ordering of two placed entries `{ date, slot }`: earlier date first,
 * then earlier SLOT within a day (slot rank from `slotRankOf(entry)` — named
 * strategy slots in order, fillers/Other last). Used so "before/after" is
 * slot-aware, not just day-aware. PURE.
 */
export function compareEntryOrder(a, b, slotRankOf = () => 0) {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  const ra = slotRankOf(a);
  const rb = slotRankOf(b);
  return ra === rb ? 0 : ra < rb ? -1 : 1;
}

/**
 * Add one serving of `recipe` to a day, mirroring the generator's placement:
 * draw an already-prepared serving from recipe-stock first (no prep), else
 * source the batch (make/buy) via sourceEntry. PURE — mutates the given pools.
 *
 * @returns { entry, ops } — the menu entry + the stock ops it produced.
 */
export function addEntry({
  recipe,
  pools,
  ingredientsById,
  recipesById,
  date,
  genId = () => crypto.randomUUID(),
  genStockId = genId,
  leftoverShelfLifeDays,
  // Kept-yield overrides to cook with (and carry onto the entry) when it has to
  // be sourced — e.g. a moved cook keeping its "Change serving count". Ignored
  // when the serving is drawn from existing leftovers (nothing is cooked).
  cookOverrides = null,
}) {
  const fromStock = consumeRecipeStock({ recipe, servings: 1, pools, genId });
  if (fromStock.entry) return { entry: fromStock.entry, ops: fromStock.ops };
  const { entry, ops } = sourceEntry({
    recipe,
    servings: 1,
    pools,
    ingredientsById,
    recipesById,
    date,
    genId,
    genStockId,
    leftoverShelfLifeDays,
    cookOverrides,
  });
  return { entry: withCookOverrides(entry, cookOverrides), ops };
}

/**
 * Carry kept-yield overrides onto a freshly sourced entry that REBUILDS an
 * existing cook (move / re-cook / promote), so its "Change serving count"
 * survives — sourceEntry honours overrides but never stores them. A copy is
 * stamped (the source entry may be mutated later); empty/absent → untouched.
 */
function withCookOverrides(entry, cookOverrides) {
  if (cookOverrides && Object.keys(cookOverrides).length > 0) {
    entry.cookOverrides = { ...cookOverrides };
  }
  return entry;
}

/**
 * Detect a temporal-ordering hazard: would drawing recipe-stock for `recipe` at
 * the target `{ date, slot }` consume a batch that is only PREPARED LATER? The
 * front recipe-stock row (what consumeRecipeStock would draw) carries
 * `preparedBy`; we resolve that to its prepared entry and compare order, which is
 * SLOT-AWARE within a day (see compareEntryOrder). PURE — reads only.
 *
 * @param slot the slot tag the new entry would fill (for same-day ordering).
 * @param slotRankOf (entry) => number — slot ordinal (named slots in order,
 *   Other/fillers last). Defaults to 0 (date-only ordering).
 * @returns { menuEntryId, date, sameDay } of the later prepared entry, or null if
 *   there's no recipe-stock, it has no preparedBy, or it's prepared at/before the
 *   target. `sameDay` is true when the prep is on the SAME day as the target (but
 *   a later slot) — the caller may auto-promote without prompting in that case.
 */
export function recipeStockPreparedAfter({ recipe, pools, menus, date, slot, slotRankOf }) {
  const bucket = pools.recipes.get(recipe.itemId);
  const front = bucket?.rows?.find((r) => r.servings > 0);
  if (!front?.preparedBy) return null;
  const target = { date, slot };
  for (const [d, menu] of menus) {
    for (const e of menu?.entries ?? []) {
      if (e.menuEntryId === front.preparedBy) {
        const prep = { date: d, slot: e.slot };
        const after = compareEntryOrder(prep, target, slotRankOf) > 0;
        if (!after) return null;
        return { menuEntryId: front.preparedBy, date: d, sameDay: d === date };
      }
    }
  }
  return null;
}

/**
 * Promote a new entry on `date` to be the COOK for `recipe`, moving the cook
 * EARLIER than an existing later prepared entry. The later cook and everything
 * that ate its batch become leftover-eaters of the new (earlier) batch, filled
 * chronologically; slots the new batch can't cover are deleted. One batch total.
 *
 * PURE. Returns { entry, ops, menuPatches, deleted } where `entry` is the new
 * cook on `date`, menuPatches replace existing entries (matched by menuEntryId),
 * and `deleted` lists slots to remove. menuPatches does NOT include the new cook
 * (the caller inserts `entry` at the chosen slot).
 */
export function promoteToCook({
  recipe,
  date,
  laterPreparedEntry,
  laterPreparedDate,
  dependents = [],
  pools,
  ingredientsById,
  recipesById,
  genId = () => crypto.randomUUID(),
  genStockId = genId,
  leftoverShelfLifeDays,
  // The new cook takes over the later cook's batch, so it inherits its kept-yield
  // overrides ("Change serving count") unless the caller supplies others.
  cookOverrides = laterPreparedEntry?.cookOverrides ?? null,
}) {
  const ops = [];

  // 1) Give back the later cook: returns its ingredients + drains its batch (so
  // the new batch doesn't double-count). No prompt — we handle dependents here.
  ops.push(...removeEntry({ entry: laterPreparedEntry, pools, leftoverEntries: [] }).ops);

  // 2) Cook the new batch on the earlier day (eat 1, make the batch, deposit the
  // surplus as recipe-stock).
  const { entry: newCook, ops: srcOps } = sourceEntry({
    recipe,
    servings: 1,
    pools,
    ingredientsById,
    recipesById,
    date,
    genId,
    genStockId,
    leftoverShelfLifeDays,
    cookOverrides,
  });
  withCookOverrides(newCook, cookOverrides);
  ops.push(...srcOps);

  // 3) The old cook becomes a fillable slot too, alongside its eaters. Fill them
  // chronologically from the new batch; overflow slots are deleted.
  const slots = [
    { date: laterPreparedDate, menuEntryId: laterPreparedEntry.menuEntryId },
    ...dependents.map((d) => ({ date: d.date, menuEntryId: d.menuEntryId })),
  ].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  const menuPatches = [];
  const deleted = [];
  for (const slot of slots) {
    const drawn = consumeRecipeStock({ recipe, servings: 1, pools, genId });
    if (drawn.entry) {
      ops.push(...drawn.ops);
      menuPatches.push({
        date: slot.date,
        entry: { ...drawn.entry, menuEntryId: slot.menuEntryId, preparedBy: newCook.menuEntryId },
      });
    } else {
      deleted.push({ date: slot.date, menuEntryId: slot.menuEntryId });
    }
  }

  return { entry: newCook, ops, menuPatches, deleted };
}

/** Deep-clone the pools so a simulation (e.g. canSource) can't mutate the real
 * pools. Rows are plain data, so a shallow copy of each row is enough. */
export function clonePools(pools) {
  const clone = (map) => {
    const out = new Map();
    for (const [ref, bucket] of map) out.set(ref, { rows: bucket.rows.map((r) => ({ ...r })) });
    return out;
  };
  return { ingredients: clone(pools.ingredients), recipes: clone(pools.recipes) };
}

/**
 * Apply a list of stock ops to an IN-MEMORY pool (mutates it), mirroring what
 * `menu.applyStockOps` does to the DB but on the `{ ingredients, recipes }` pool.
 *
 * Used by the deferred-commit undo path: when a user undoes an eat/prepare BEFORE
 * its stock write has committed, we hand the *inverted* ops here to give the drawn
 * stock back to the live pool (so a subsequent action sources correctly), without
 * ever touching the DB. Op → pool effect:
 *   • consumeIngredient / consumeRecipeStock  -> DRAW from the matching bucket
 *   • returnIngredient  / returnRecipeStock   -> DEPOSIT back into the bucket
 *   • addIngredientStock / addRecipeStock     -> DEPOSIT (a fresh row)
 *   • buy                                     -> no pool effect (shopping-only)
 *
 * Draw ops target a specific `stockRef` row when present (soonest-expiry order is
 * otherwise preserved). Deposits recreate a row keyed by `stockRef` so a later
 * draw/return round-trips cleanly. PURE aside from mutating `pools`.
 */
export function applyOpsToPool(pools, ops) {
  for (const op of ops ?? []) {
    const servings = Number(op.servings) || 0;
    if (servings <= 0 && op.type !== 'buy') continue;
    if (op.type === 'consumeIngredient' || op.type === 'consumeRecipeStock') {
      const map = op.type === 'consumeIngredient' ? pools.ingredients : pools.recipes;
      const ref = op.ingredientRef ?? op.recipeRef;
      drawStockRefFromPool(map, ref, op.stockRef, servings);
    } else if (op.type === 'returnIngredient' || op.type === 'addIngredientStock') {
      depositToPool(pools.ingredients, op.ingredientRef, {
        stockRef: op.stockRef,
        servings,
        expiry: op.expiry ?? null,
      });
    } else if (op.type === 'returnRecipeStock' || op.type === 'addRecipeStock') {
      depositToPool(pools.recipes, op.recipeRef, {
        stockRef: op.stockRef,
        servings,
        expiry: op.expiry ?? null,
        ...(op.preparedBy ? { preparedBy: op.preparedBy } : {}),
      });
    }
    // 'buy' ops: shopping-list only — no pool/stock effect.
  }
  return pools;
}

/**
 * Draw `need` servings from a specific `stockRef` row of a pool bucket (falling
 * back to soonest-expiry-first across the bucket if that exact row isn't found —
 * e.g. it was already partly consumed). Mutates the bucket. Helper for
 * `applyOpsToPool`.
 */
function drawStockRefFromPool(map, ref, stockRef, need) {
  const bucket = ref ? map.get(ref) : null;
  // Fast path: known bucket.
  if (bucket) {
    if (stockRef) {
      const row = bucket.rows.find((r) => r.stockRef === stockRef);
      if (row) {
        row.servings -= Math.min(row.servings, need);
        if (row.servings <= 0) bucket.rows = bucket.rows.filter((r) => r !== row);
        if (bucket.rows.length === 0) map.delete(ref);
        return;
      }
      // stockRef not in this bucket → it may have been merged/re-keyed; fall back
      // to soonest-expiry within the SAME bucket (preserves prior behaviour).
    }
    drawFromPool(bucket, need);
    if (bucket.rows.length === 0) map.delete(ref);
    return;
  }
  // Defense-in-depth: ref missing/unknown — find the stockRef row across ALL
  // buckets so a producer that forgot to stamp the ref can't silently no-op the
  // draw (that was the root of a prepare-undo pool leak).
  if (!stockRef) return;
  for (const [key, b] of map) {
    const row = b.rows.find((r) => r.stockRef === stockRef);
    if (!row) continue;
    row.servings -= Math.min(row.servings, need);
    if (row.servings <= 0) b.rows = b.rows.filter((r) => r !== row);
    if (b.rows.length === 0) map.delete(key);
    return;
  }
}

/**
 * Can this recipe be sourced right now — drawn from recipe-stock, made from
 * ingredient stock, and/or bought? Returns false only when a component ingredient
 * is unbuyable (buyServings:0) AND not available in stock. PURE: simulates
 * against a CLONE of the pools so nothing is mutated.
 */
/**
 * Derive how much of each stock row is RESERVED by the current (uneaten) menu
 * plan — a pure, read-only computation used by the real-time stock view and the
 * baseline migration. It re-runs the SAME sourcing path the generator/edit
 * session use (leftovers-first, then draw ingredient stock, then buy/cook the
 * shortfall) over a throwaway copy of the pools, and counts only the servings
 * DRAWN FROM EXISTING STOCK (the `consume*` ops) — buys and fresh cooks are
 * shortfall, not a reservation against on-hand stock.
 *
 * Why re-source instead of reading each entry's stored `fromStock` provenance:
 * a saved entry's `stockRef`s point at the stock rows chosen when it was created,
 * which under today's projection model may already be consumed/deleted — so they
 * won't line up with CURRENT stock. Re-sourcing against live stock yields the
 * reservation that actually holds now (and stays correct post-migration).
 *
 * Entries are processed in menu order (date asc, then slot rank) so soonest
 * days reserve the soonest-expiry stock first, matching generation. Skipped:
 * empty placeholders (no recipeRef) and fully-eaten entries (their stock is
 * already gone). Partially-eaten entries reserve only their REMAINING servings.
 *
 * @param menus  Array|Map of menu day objects ({ entries: [...] , date? }).
 * @param stock  the raw stock rows (as listStock returns).
 * @param ingredientsById Map<ingredientRef, ingredient>
 * @param recipesById     Map<recipeRef, recipe> — MUST include virtual
 *                        `recipe#ingredient:` recipes so every entry resolves.
 * @param slotRankOf      (slotTag) => number, for intra-day ordering (optional).
 * @returns {{
 *   byStockRef: Map<stockRef, servings>,   // reserved per physical stock row
 *   byIngredient: Map<ingredientRef, servings>,
 *   byRecipe: Map<recipeRef, servings>,
 *   unsourced: Array<{ ref, servings }>,   // plan needs beyond stock+buy/cook
 * }}
 */
export function reservationsFromMenus(
  menus,
  stock,
  ingredientsById,
  recipesById,
  { slotRankOf = () => 0 } = {},
) {
  const byStockRef = new Map();
  const byIngredient = new Map();
  const byRecipe = new Map();
  const unsourced = [];
  // Per-entry sourcing summary (for impact diffs — see stockRemovalImpact):
  // { date, menuEntryId, recipeRef, servings, fromLeftovers, fromStock, made,
  //   buys: Map<ingredientRef, packs>, unsourced }.
  const byEntry = [];

  const pools = poolsFromStock(stock, ingredientsById);

  // Snapshot the on-hand servings per stock row BEFORE any draws, so we can map a
  // post-draw depletion back to reserved-per-row. Simpler: accumulate directly
  // from the consume ops, which each name a stockRef + servings.
  const addRef = (map, key, n) => {
    if (!key || !(n > 0)) return;
    map.set(key, (map.get(key) ?? 0) + n);
  };

  for (const day of sortedMenuDays(menus)) {
    const entries = orderedDayEntries(day, slotRankOf); // skips empty placeholders

    for (const entry of entries) {
      const remaining = remainingServingsOf(entry);
      if (remaining <= 0) continue; // fully eaten — its stock is already gone
      const recipe = recipesById?.get?.(entry.recipeRef);
      if (!recipe) continue; // unresolved recipe — can't source; leave unreserved

      // Prefer existing recipe-stock (leftovers) first, exactly like the menu
      // page; fall back to a full source (draw ingredients / buy / cook) for
      // whatever the leftovers didn't cover.
      const ops = [];
      const drawn = consumeRecipeStock({ recipe, servings: remaining, pools, genId: () => 'x' });
      const fromLeftovers = drawn.entry ? drawn.entry.servings : 0;
      if (drawn.entry) ops.push(...drawn.ops);
      const short = remaining - fromLeftovers;
      let entryUnsourced = 0;
      // PREPARED-AWARE: an already-prepared cook entry's batch was cooked at
      // prepare time — its ingredients are GONE and its servings live as
      // recipe-stock (drawn just above). It must NOT be re-cooked/re-sourced here,
      // or we'd double-count phantom ingredient reservations. So only fall through
      // to full sourcing for entries that are NOT prepared. (A leftover entry has
      // nothing to cook anyway; a prepared cook draws only from its own batch.)
      if (short > 0 && !isPrepared(entry)) {
        const res = sourceEntry({
          recipe,
          servings: short,
          pools,
          ingredientsById,
          recipesById,
          date: '1970-01-01',
          // Honour per-recipe kept-yield overrides so a cook that will keep FEWER
          // servings deposits less recipe-stock here — its leftover-eaters then
          // correctly surface as unsourced if the plan over-commits.
          cookOverrides: entry.cookOverrides ?? null,
        });
        ops.push(...res.ops);
        for (const u of res.unsourced) {
          unsourced.push(u);
          entryUnsourced += Number(u.servings) || 0;
        }
      }

      // Count only draws from EXISTING stock as reservations. A `buy`/
      // `addIngredientStock`/`addRecipeStock` op creates new rows for the
      // shortfall — those aren't reserved against current on-hand stock, so
      // their subsequent consume ops (same stockRef) must be excluded.
      const freshRefs = new Set();
      for (const op of ops) {
        if (op.type === 'addIngredientStock' || op.type === 'addRecipeStock') {
          freshRefs.add(op.stockRef);
        }
      }
      let fromStock = 0;
      const buys = new Map();
      for (const op of ops) {
        const isDraw =
          (op.type === 'consumeIngredient' || op.type === 'consumeRecipeStock') &&
          !freshRefs.has(op.stockRef);
        if (isDraw) {
          addRef(byStockRef, op.stockRef, op.servings);
          fromStock += Number(op.servings) || 0;
        }
        if (op.type === 'buy') addRef(buys, op.ingredientRef, Number(op.packs) || 0);
      }
      byEntry.push({
        date: entry.date ?? day.date ?? null,
        menuEntryId: entry.menuEntryId ?? null,
        recipeRef: entry.recipeRef,
        servings: remaining,
        fromLeftovers,
        fromStock,
        // Servings that must be newly made (cooked / assembled) rather than
        // eaten from existing leftovers.
        made: short > 0 && !isPrepared(entry) ? short : 0,
        buys,
        unsourced: entryUnsourced,
      });
    }
  }

  // Roll up per-row reservations to ingredient/recipe totals for display.
  const rowRef = new Map(); // stockRef -> { ingredientRef?, recipeRef? }
  for (const s of stock ?? []) {
    rowRef.set(s.itemId, { ingredientRef: s.ingredientRef, recipeRef: s.recipeRef });
  }
  for (const [stockRef, servings] of byStockRef) {
    const meta = rowRef.get(stockRef);
    if (!meta) continue;
    if (meta.ingredientRef) addRef(byIngredient, meta.ingredientRef, servings);
    else if (meta.recipeRef) addRef(byRecipe, meta.recipeRef, servings);
  }

  return { byStockRef, byIngredient, byRecipe, unsourced, byEntry };
}

/**
 * What removing stock would do to the live menu plan. Re-runs the reservation
 * sourcing pass (the same path the menu page sources by) over `stock` before and
 * after taking `servings` off row `itemId`, and diffs each menu entry.
 *
 * `covered` is true when nothing about any entry's sourcing changes (the plan
 * just draws the same item from other rows). Otherwise `affected` lists each
 * changed entry with its most significant consequence:
 *   'unsourced' — can no longer be fully sourced (not in stock, can't be bought)
 *   'shopping'  — now needs more bought (`newBuys`: Map<ingredientRef, extra packs>)
 *   'make'      — now made fresh instead of eaten from leftovers (stock covers it)
 * `wantedAfter` is the whole-plan buy list after the removal (buysFromMenus
 * shape) — feed it to the shopping-list preview / reconcile.
 *
 * PURE. Rows are matched by `itemId`; removing ≥ the row's servings drops it.
 */
export function stockRemovalImpact({
  menus,
  stock,
  itemId,
  servings,
  ingredientsById,
  recipesById,
  slotRankOf,
}) {
  const take = Math.max(0, Number(servings) || 0);
  const after = [];
  for (const row of stock ?? []) {
    if (row.itemId !== itemId) {
      after.push(row);
      continue;
    }
    const left = (Number(row.servings) || 0) - take;
    if (left > 0) after.push({ ...row, servings: left });
  }
  const opts = { slotRankOf };
  const before = reservationsFromMenus(menus, stock, ingredientsById, recipesById, opts);
  const post = reservationsFromMenus(menus, after, ingredientsById, recipesById, opts);

  const keyOf = (e, i) => e.menuEntryId ?? `${e.date}#${i}`;
  const beforeByKey = new Map(before.byEntry.map((e, i) => [keyOf(e, i), e]));
  const affected = [];
  post.byEntry.forEach((a, i) => {
    const b = beforeByKey.get(keyOf(a, i));
    if (!b) return;
    const newBuys = new Map();
    for (const [ref, packs] of a.buys) {
      const extra = packs - (b.buys.get(ref) ?? 0);
      if (extra > 0) newBuys.set(ref, extra);
    }
    let effect = null;
    if (a.unsourced > b.unsourced) effect = 'unsourced';
    else if (newBuys.size > 0) effect = 'shopping';
    else if (a.made > b.made) effect = 'make';
    if (effect) {
      affected.push({
        date: a.date,
        menuEntryId: a.menuEntryId,
        recipeRef: a.recipeRef,
        effect,
        newBuys,
        unsourcedServings: a.unsourced - b.unsourced,
      });
    }
  });

  return {
    covered: affected.length === 0,
    affected,
    wantedAfter: buysFromMenus(menus, after, ingredientsById, recipesById, opts),
  };
}

/**
 * Derive the net BUY list the WHOLE current plan needs against current stock —
 * the correct source for persisting the menu's shopping list on save.
 *
 * WHY not the edit session's `ops`: `state.edit.ops` starts empty and only logs
 * the CURRENT session's incremental edits. Editing a menu generated in a PRIOR
 * session has no `buy` ops, so deriving "wanted" from `ops` wrongly yields `[]`
 * and the reconcile deletes still-needed rows. Re-sourcing the whole plan here is
 * independent of when the menu was generated, and is inherently idempotent.
 *
 * Mirrors `reservationsFromMenus` (same sourcing path, same prepared-awareness,
 * same menu ordering) but keeps the `buy` ops instead of the stock draws, netted
 * per ingredient (packs summed) — same shape as `menu.shoppingListFromOps`.
 *
 * @returns Array<{ ingredientRef, name, packs, buyServings, buyUnit }>
 */
export function buysFromMenus(
  menus,
  stock,
  ingredientsById,
  recipesById,
  { slotRankOf = () => 0 } = {},
) {
  const pools = poolsFromStock(stock, ingredientsById);
  const byRef = new Map(); // ingredientRef -> { packs, buyServings }

  for (const day of sortedMenuDays(menus)) {
    const entries = orderedDayEntries(day, slotRankOf);

    for (const entry of entries) {
      const remaining = remainingServingsOf(entry);
      if (remaining <= 0) continue;
      const recipe = recipesById?.get?.(entry.recipeRef);
      if (!recipe) continue;

      // Leftovers first (same as reservations / the menu page), then source the
      // shortfall — collecting the buy ops it emits.
      const ops = [];
      const drawn = consumeRecipeStock({ recipe, servings: remaining, pools, genId: () => 'x' });
      const fromLeftovers = drawn.entry ? drawn.entry.servings : 0;
      if (drawn.entry) ops.push(...drawn.ops);
      const short = remaining - fromLeftovers;
      // A prepared cook was already cooked — no ingredients to buy. Only source
      // (and thus buy) for not-yet-prepared entries.
      if (short > 0 && !isPrepared(entry)) {
        const res = sourceEntry({
          recipe,
          servings: short,
          pools,
          ingredientsById,
          recipesById,
          date: '1970-01-01',
          // Same kept-yield overrides as reservations: keeping fewer servings per
          // batch means more batches are needed to feed the plan's eaters, so the
          // buy list must reflect it.
          cookOverrides: entry.cookOverrides ?? null,
        });
        ops.push(...res.ops);
      }

      for (const op of ops) {
        if (op.type !== 'buy') continue;
        const cur = byRef.get(op.ingredientRef) ?? { packs: 0, buyServings: op.buyServings };
        cur.packs += Number(op.packs) || 0;
        byRef.set(op.ingredientRef, cur);
      }
    }
  }

  const out = [];
  for (const [ingredientRef, { packs, buyServings }] of byRef) {
    if (!(packs > 0)) continue;
    const ing = ingredientsById?.get?.(ingredientRef);
    out.push({
      ingredientRef,
      name: ing?.name ?? ingredientRef,
      packs,
      buyServings: buyServings ?? ing?.buyServings ?? 1,
      buyUnit: ing?.buyUnit ?? null,
    });
  }
  return out;
}

export function canSource({ recipe, pools, ingredientsById, recipesById }) {
  const sim = clonePools(pools);
  // A leftover already in recipe-stock is trivially sourceable.
  const recipeBucket = sim.recipes.get(recipe.itemId);
  if (recipeBucket && recipeBucket.rows.some((r) => r.servings > 0)) return true;
  const { unsourced } = sourceEntry({
    recipe,
    servings: 1,
    pools: sim,
    ingredientsById,
    recipesById,
    date: '1970-01-01',
  });
  return unsourced.length === 0;
}

/**
 * True if at least one serving of this recipe is AVAILABLE in stock right now —
 * i.e. on-hand minus what the current plan already RESERVES. A lighter check
 * than `canSource` (which would make/buy the shortfall). Used for the picker's
 * "In stock" badge.
 *
 * For a real recipe this checks its LEFTOVER recipe-stock only; it deliberately
 * does NOT inspect the recipe's components (a recipe isn't "in stock" just
 * because its ingredients are). For an ingredient-virtual recipe
 * (`recipe#ingredient:<id>`) it checks the source ingredient's own stock, since
 * such a recipe IS a single ingredient serving.
 *
 * @param reservations optional `{ byIngredient, byRecipe }` (Maps of ref →
 *   reserved servings) from `reservationsFromMenus`; subtracted from on-hand so
 *   already-reserved stock doesn't count as available.
 */
export function isFullyInStock(recipe, pools, reservations = null) {
  if (!recipe || !pools) return false;
  const isVirtual = isIngredientRecipeId(recipe.itemId);
  const ref = isVirtual ? recipe.itemId.slice(INGREDIENT_RECIPE_PREFIX.length) : recipe.itemId;
  const bucket = isVirtual ? pools.ingredients?.get(ref) : pools.recipes?.get(ref);
  if (!bucket) return false;
  const onHand = bucket.rows.reduce((sum, r) => sum + (Number(r.servings) || 0), 0);
  const reservedMap = isVirtual ? reservations?.byIngredient : reservations?.byRecipe;
  const reserved = Number(reservedMap?.get?.(ref)) || 0;
  return onHand - reserved >= 1;
}

/** Total pooled servings of a ref (0 if absent). */
function poolServings(map, ref) {
  const bucket = map?.get?.(ref);
  if (!bucket) return 0;
  return bucket.rows.reduce((sum, r) => sum + (Number(r.servings) || 0), 0);
}

/** The YYYY-MM-DD date of a menu-day object. The day date is NOT stored as a
 *  `date` field — it's the Map key / encoded in the `menu#<date>` itemId — so we
 *  derive it (falling back to an explicit `date` if a caller supplied one). */
function dayDateOf(day) {
  if (day?.date) return String(day.date);
  const id = String(day?.itemId ?? '');
  const i = id.indexOf('#');
  return i >= 0 ? id.slice(i + 1) : id;
}

/** All menu days sorted ascending by date (array or Map input). Each returned
 *  object is the original day with a resolved `date` attached for downstream use. */
function sortedMenuDays(menus) {
  // For a Map input the KEY is the authoritative date: days created during an
  // edit session (`{ entries: [] }`) carry neither `date` nor `itemId`, and
  // without the key they'd sort FIRST — replaying their leftover-eaters before the
  // cook they draw from (fabricating a phantom cook + phantom leftovers).
  const pairs = Array.isArray(menus)
    ? menus.map((m) => [null, m])
    : [...(menus?.entries?.() ?? [])];
  return pairs
    .filter(([, m]) => m && Array.isArray(m.entries))
    .map(([key, m]) => (m.date ? m : { ...m, date: key ? String(key) : dayDateOf(m) }))
    .sort((a, b) => String(a.date ?? '').localeCompare(String(b.date ?? '')));
}

/** A day's sourceable entries in slot order, each with its day's `date` resolved
 *  (so `slotRankOf` / ordering see the right day). Copies; never mutates. */
function orderedDayEntries(day, slotRankOf) {
  return day.entries
    .filter((e) => e && e.recipeRef)
    .map((e) => (e.date ? e : { ...e, date: day.date }))
    .sort((a, b) => compareEntryOrder(a, b, slotRankOf));
}

/**
 * Source ONE menu entry against `pools` using the REAL sourcing machinery
 * (leftovers-first, then a full recursive source of the shortfall). This mutates
 * `pools` exactly like the generator/reservation passes — depositing surplus at
 * every nesting level, so a parent recipe's cook leaves its SUB-recipe leftovers
 * in `pools.recipes[subRef]`. Returns nothing; the caller reads the pools.
 */
/**
 * Project the stock pools FORWARD through the plan: start from current on-hand
 * `stock`, then play every menu day up to and including `throughDate` (in date
 * order) through the same sourcing pass the generator uses — drawing leftovers,
 * cooking unprepared batches (which DEPOSITS their surplus as recipe-stock), and
 * consuming ingredients. The returned pools are "what's on hand after those days
 * have sourced their meals".
 *
 * This is the correct basis for "what would generation see when filling day X":
 * it reflects surpluses the plan's own cooking CREATES (e.g. an uncooked 6-serving
 * batch that leaves 3 leftover servings after its eaters), which a plain
 * current-stock reservation can't represent.
 *
 * @param throughDate inclusive upper bound (YYYY-MM-DD); null/undefined = all days
 * @returns { ingredients: Map, recipes: Map } residual pools (a fresh object)
 */
export function poolsAfterMenus(
  menus,
  stock,
  ingredientsById,
  recipesById,
  { throughDate = null, slotRankOf = () => 0 } = {},
) {
  const pools = poolsFromStock(stock, ingredientsById);
  const days = sortedMenuDays(menus);
  for (const day of days) {
    if (throughDate && String(day.date) > String(throughDate)) break; // days are sorted asc
    for (const entry of orderedDayEntries(day, slotRankOf)) {
      const recipe = recipesById?.get?.(entry.recipeRef);
      if (!recipe) continue; // unresolved — can't source; leave pools untouched
      sourceEntryIntoPools(entry, recipe, pools, ingredientsById, recipesById);
    }
  }
  return pools;
}

/**
 * The edit-session / generator BASELINE pools for a plan: the stock genuinely
 * AVAILABLE to new meals once every existing menu entry has been sourced.
 *
 *   • Ingredient pool — current on-hand ingredient stock minus what the plan
 *     draws from it (equivalent to `availableStock(stock, reservations)`). Packs
 *     the plan would BUY are stripped back out: buys are re-derived for the
 *     shopping list on save, so new meals don't silently piggy-back on them.
 *   • Recipe pool — on-hand recipe-stock minus what the plan eats, PLUS the
 *     not-yet-eaten leftovers of every PLANNED (unprepared) cook. Those batches
 *     only exist on paper until the user taps Prepare, but adding a meal must
 *     still see them as leftovers (otherwise it spawns a second cook, even though
 *     the picker — via `availableAtSlot` — badges the recipe "In stock").
 *
 * Planned batches are deposited under their REAL identity — the `stockRef` +
 * `preparedBy` recorded on the cook entry's `cookedBatches` (top-level
 * `preparedBy` = the cook's menuEntryId) — so the temporal-order hazard
 * (`recipeStockPreparedAfter`), `removeEntry`'s batch drain and leftover
 * linking all resolve against the actual cook. Expiry is the cook day + shelf
 * life. PURE: builds its own pools.
 *
 * @returns { ingredients: Map, recipes: Map }
 */
export function editPoolsFromMenus(
  menus,
  stock,
  ingredientsById,
  recipesById,
  { slotRankOf = () => 0, leftoverShelfLifeDays } = {},
) {
  const pools = poolsFromStock(stock, ingredientsById);
  let seq = 0;
  const tmpId = () => `tmp-planned-${seq++}`;

  for (const day of sortedMenuDays(menus)) {
    for (const entry of orderedDayEntries(day, slotRankOf)) {
      const remaining = remainingServingsOf(entry);
      if (remaining <= 0) continue;
      const recipe = recipesById?.get?.(entry.recipeRef);
      if (!recipe) continue;

      const drawn = consumeRecipeStock({ recipe, servings: remaining, pools, genId: tmpId });
      const short = remaining - (drawn.entry ? drawn.entry.servings : 0);
      if (short <= 0 || isPrepared(entry)) continue;

      // Replay the cook with the entry's own id so the top-level batch is linked
      // (preparedBy) to the real cook entry.
      let first = true;
      const res = sourceEntry({
        recipe,
        servings: short,
        pools,
        ingredientsById,
        recipesById,
        date: day.date,
        genId: () => {
          if (first && entry.menuEntryId) {
            first = false;
            return entry.menuEntryId;
          }
          return tmpId();
        },
        genStockId: tmpId,
        leftoverShelfLifeDays,
        cookOverrides: entry.cookOverrides ?? null,
      });

      // Re-key each replayed batch to the stockRef/preparedBy recorded on the
      // cook entry (matched by recipeRef, in order), so it is the SAME batch the
      // entry's leftover-eaters and removal logic point at.
      const unused = [...(entry.cookedBatches ?? [])];
      for (const op of res.ops) {
        if (op.type === 'addIngredientStock') {
          removeStockRefFromPool(pools.ingredients, op.ingredientRef, op.stockRef);
        } else if (op.type === 'addRecipeStock') {
          const i = unused.findIndex((b) => b.recipeRef === op.recipeRef);
          if (i < 0) continue;
          const [batch] = unused.splice(i, 1);
          const row = pools.recipes.get(op.recipeRef)?.rows.find((r) => r.stockRef === op.stockRef);
          if (!row) continue;
          row.stockRef = batch.stockRef;
          if (batch.preparedBy) row.preparedBy = batch.preparedBy;
        }
      }
    }
  }
  return pools;
}

function sourceEntryIntoPools(entry, recipe, pools, ingredientsById, recipesById) {
  const remaining = remainingServingsOf(entry);
  if (remaining <= 0) return;
  const drawn = consumeRecipeStock({ recipe, servings: remaining, pools, genId: () => 'x' });
  const short = remaining - (drawn.entry ? drawn.entry.servings : 0);
  // A prepared cook's batch already exists in stock (its ingredients are spent);
  // it must NOT be re-sourced or we'd fabricate phantom cooks. Leftover-eaters
  // have nothing to cook. Only non-prepared entries fall through to full sourcing.
  if (short > 0 && !isPrepared(entry)) {
    sourceEntry({
      recipe,
      servings: short,
      pools,
      ingredientsById,
      recipesById,
      date: '1970-01-01',
      // Honour per-recipe kept-yield overrides ("Change serving count") so a cook
      // that keeps FEWER servings deposits proportionally less leftover recipe-
      // stock into the replayed pool. Without this the replay models every cook at
      // the recipe's default servingsMade, fabricating phantom leftovers — which
      // wrongly lit the picker's "In stock" badge and over-stated poolsAfterMenus.
      cookOverrides: entry.cookOverrides ?? null,
    });
  }
}

/**
 * How many servings of `recipe` are AVAILABLE to add at a target slot on
 * `date` — WITHOUT any new cooking beyond what the current plan already does.
 * Fully TIME-AWARE and handles NESTED (sub-)recipes, because it simulates the
 * plan with the real sourcing machinery and snapshots the resulting stock pool at
 * the target-day boundary.
 *
 * Two behaviours fall out naturally:
 *   • INGREDIENTS: sourcing buys whole packs up front and draws as needed, so an
 *     ingredient-virtual recipe (`recipe#ingredient:<id>`) reflects the plan's net
 *     spare pack servings. (Time has little effect — shopping is assumed up front —
 *     which matches the agreed model.)
 *   • COOKED RECIPES (incl. sub-recipes cooked as a byproduct of a parent): a
 *     leftover only exists once its batch is cooked, so a batch produced on a day
 *     AFTER the target contributes nothing at the target (adding there spawns a
 *     fresh cook). We process entries in date+slot order and SNAPSHOT the pool the
 *     moment we cross past the target day; then we let the remaining (after-target)
 *     entries draw, and take the MINIMUM of the snapshot and the running pool so a
 *     later meal that consumes the surplus removes it from the offer (conservative).
 *
 * PURE: builds its own pools; never mutates the caller's data.
 *
 * @param {string} params.date              target slot date (YYYY-MM-DD)
 * @param {Function} [params.slotRankOf]    slot-ordering fn (same as elsewhere)
 * @param {string} [params.excludeEntryId]  a menuEntryId to ignore (the entry
 *                                          being replaced, so it doesn't count
 *                                          against itself)
 * @returns {number} available servings (>= 0)
 */
export function availableAtSlot(
  recipe,
  menus,
  stock,
  ingredientsById,
  recipesById,
  { date, slotRankOf = () => 0, excludeEntryId = null } = {},
) {
  if (!recipe) return 0;
  const targetDate = String(date ?? '');
  const isVirtual = isIngredientRecipeId(recipe.itemId);
  const ref = isVirtual ? recipe.itemId.slice(INGREDIENT_RECIPE_PREFIX.length) : recipe.itemId;
  const readPool = (pools) =>
    isVirtual ? poolServings(pools.ingredients, ref) : poolServings(pools.recipes, ref);

  const pools = poolsFromStock(stock, ingredientsById);

  // Flatten all entries into strict (date, slot) order, resolving each day's date.
  const entries = [];
  for (const day of sortedMenuDays(menus)) {
    for (const entry of day.entries) {
      if (!entry || !entry.recipeRef || entry.menuEntryId === excludeEntryId) continue;
      entries.push({ ...entry, date: entry.date ?? day.date });
    }
  }
  entries.sort((a, b) => compareEntryOrder(a, b, slotRankOf));

  // Walk the plan; snapshot the target pool the moment we cross past the target
  // day, then keep the running minimum so an after-target consumer of the surplus
  // reduces the offer (conservative — never steal from a later meal).
  let snapshot = null;
  for (const entry of entries) {
    if (snapshot === null && String(entry.date ?? '') > targetDate) snapshot = readPool(pools);
    const r = recipesById?.get?.(entry.recipeRef);
    if (r) sourceEntryIntoPools(entry, r, pools, ingredientsById, recipesById);
    if (snapshot !== null) snapshot = Math.min(snapshot, readPool(pools));
  }
  // No entries after the target → the final pool IS the availability.
  if (snapshot === null) snapshot = readPool(pools);
  return Math.max(0, snapshot);
}

/**
 * Reduce a raw stock list by what a plan already RESERVES, returning the stock
 * that is genuinely AVAILABLE. Each row's servings are decremented by its
 * `byStockRef` reservation (from `reservationsFromMenus`); rows drained to zero
 * (or below) are dropped.
 *
 * Use this to feed the generator only the leftover/ingredient stock that existing
 * saved menus have NOT already claimed — otherwise it would re-allocate a
 * leftover serving that another day is already eating (double-counting).
 *
 * @param stock array of stock rows ({ itemId, servings, ... })
 * @param byStockRef Map<stockRef(=itemId), reservedServings>
 * @returns a NEW array of rows (copies) with reduced servings, zeros removed
 */
export function availableStock(stock, byStockRef) {
  const reserved = byStockRef ?? new Map();
  const out = [];
  for (const row of stock ?? []) {
    const claim = Number(reserved.get?.(row.itemId)) || 0;
    const left = (Number(row.servings) || 0) - claim;
    if (left > 0) out.push({ ...row, servings: left });
  }
  return out;
}

/** Merge multiple draws on the same stockRef into one fromStock entry. */
function mergeDraws(draws) {
  const byRef = new Map();
  for (const d of draws) {
    byRef.set(d.stockRef, (byRef.get(d.stockRef) ?? 0) + d.servings);
  }
  return [...byRef].map(([stockRef, servings]) => ({ stockRef, servings }));
}

/** Expiry Date for freshly-bought ingredient stock (its default shelf life). */
function ingredientExpiryFor(ingredient, date) {
  const days = ingredient?.shelfLifeDays;
  if (days === null || days === undefined) return null; // indefinite
  return addDays(date, Number(days));
}

/** Add `days` to a YYYY-MM-DD date, returning a local-midnight Date (or null). */
function addDays(date, days) {
  if (days === null || days === undefined || !date) return null;
  const base = new Date(`${date}T00:00:00`);
  if (Number.isNaN(base.getTime())) return null;
  return new Date(base.getTime() + Number(days) * 24 * 60 * 60 * 1000);
}

/** Default shelf life (days) for leftover recipe-stock (mirrors menu.js). */
export const DEFAULT_LEFTOVER_DAYS = 4;

const SHIFT_MS_PER_DAY = 24 * 60 * 60 * 1000;
const nextDate = (date) => {
  const d = new Date(`${date}T00:00:00`);
  d.setTime(d.getTime() + SHIFT_MS_PER_DAY);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Plan a "fill nearest gaps" cascade for inserting new menu days into a set of
 * existing dates. PURE — takes plain YYYY-MM-DD strings, returns a mapping.
 *
 * When `newDates` collide with `existingDates`, each colliding existing day is
 * pushed forward one calendar day at a time until it lands on a free date,
 * cascading (a pushed day can collide with the next existing day, pushing it in
 * turn). We only push as far as necessary: the walk stops once no occupied date
 * clashes with a new date or with another (already-relocated) day. Interior gaps
 * in the existing schedule ABSORB the shift — days after a gap may not move.
 *
 * @param existingDates iterable of YYYY-MM-DD strings that currently have a menu.
 * @param newDates iterable of YYYY-MM-DD strings being inserted.
 * @returns Map<oldDate, newDate> for ONLY the days that must move (unchanged days
 *   are omitted). The mapping is guaranteed collision-free: the resulting set of
 *   {unmoved existing} ∪ {relocated} ∪ {newDates} has no duplicate date.
 */
export function planDayShift(existingDates, newDates) {
  const newSet = new Set(newDates);
  // Every existing day, earliest first. A new day landing on an existing date
  // DISPLACES that existing day (it doesn't overwrite it) — so we consider all
  // existing days as candidates to push, anchored around the fixed new days.
  const existing = [...new Set(existingDates)].sort();

  // `occupied` = every date that will be taken once the new days are in place;
  // seed it with the new days (fixed anchors we must not collide with).
  const occupied = new Set(newSet);
  const moves = new Map();

  for (const orig of existing) {
    // Where does this day end up? If its current date is free (not taken by a
    // new day or an already-relocated day) it stays; otherwise walk forward to
    // the next free date.
    let target = orig;
    while (occupied.has(target)) target = nextDate(target);
    occupied.add(target);
    if (target !== orig) moves.set(orig, target);
  }
  return moves;
}

/**
 * Remove a menu ENTRY, giving its consumed stock back to the pools and emitting
 * the inverse stock ops. PURE (mutates the given pools only).
 *
 * Give-back rules:
 *   • each ingredient's `fromStock` rows are returned (returnIngredient) so the
 *     stock is available again; `pantry` ingredients give nothing back.
 *   • if the entry consumed a leftover batch (`preparedBy`/`fromRecipeStock`),
 *     those recipe-stock servings are returned (returnRecipeStock).
 *   • if the entry is the PREPARED entry that feeds a leftover batch, removing it
 *     would orphan the leftover menu entries. We DON'T silently delete them — the
 *     module stays pure and returns a `prompt` describing the choice (promote a
 *     leftover menu entry to prepare the batch, or delete them). The caller
 *     resolves it via resolvePreparedEntryRemoval. (The prepared entry's own
 *     ingredient give-back is still emitted; the leftover-batch fate is decided by
 *     the prompt.)
 *
 * @param leftoverEntries menu entries elsewhere in the run whose `preparedBy`
 *   equals this entry's `menuEntryId` (the caller supplies them; removeEntry
 *   doesn't scan the menus).
 * @returns { ops, prompt? } — prompt =
 *   { type:'preparedEntryRemoval', menuEntryId, leftoverEntries }
 */
export function removeEntry({ entry, pools, leftoverEntries = [] }) {
  const ops = [];
  if (!entry) return { ops };

  // Real-time stock: a PREPARED entry's batch was already cooked at Prepare
  // time — its ingredients are gone and its servings physically exist as
  // recipe-stock rows. Removing the menu entry must NOT drain those leftovers or
  // give back ingredients; the cooked food simply becomes FREE stock (no longer
  // reserved). The cook's own uneaten serving is one of those recipe-stock rows,
  // so it too falls to free. Leftover entries on other days still point at the
  // same batch and stay valid (no delete/promote prompt). So: no stock ops.
  // (Already-eaten servings stay eaten — energy banked.)
  if (isPrepared(entry)) {
    return { ops };
  }

  // 0) If this entry prepared a batch that OTHER days still eat, surface a prompt
  // FIRST and mutate nothing — so a caller that cancels leaves stock/menus
  // untouched. The actual give-back happens on confirm (the caller re-invokes the
  // removal once the user resolves the prompt). This is a pure read.
  if (leftoverServingsOf(entry) > 0 && leftoverEntries.length > 0) {
    return {
      ops,
      prompt: {
        type: 'preparedEntryRemoval',
        menuEntryId: entry.menuEntryId,
        leftoverEntries,
      },
    };
  }

  // Only UN-eaten servings come back to stock — anything already eaten is gone for
  // good. Scale each give-back draw by remaining/servings. (For a prepared cook
  // the surplus leftovers are handled separately in step 3; this factor applies to
  // the give-back draws in steps 1 & 2.)
  const servings = Math.max(0, Number(entry.servings) || 0);
  const remaining = remainingServingsOf(entry);
  const keepFactor = servings > 0 ? remaining / servings : 1;

  // 1) Return each ingredient's drawn stock to the pool. We carry the
  // ingredientRef on the op so the applier can RECREATE the stock row if it was
  // fully consumed (and therefore deleted) at finalise — a returnIngredient that
  // lands on a missing row otherwise can't reconstruct it.
  for (const ing of entry.ingredients ?? []) {
    if (ing.pantryStaple) continue;
    for (const d of ing.fromStock ?? []) {
      const give = (Number(d.servings) || 0) * keepFactor;
      if (give <= 0) continue; // fully eaten — nothing to return
      ops.push({
        type: 'returnIngredient',
        stockRef: d.stockRef,
        servings: give,
        ingredientRef: ing.ingredientRef,
      });
      depositToPool(pools.ingredients, ing.ingredientRef, {
        stockRef: d.stockRef,
        servings: give,
        // Expiry unknown on give-back; treat as already-pooled (front of queue).
        expiry: null,
      });
    }
  }

  // 2) Return any recipe-stock this entry consumed (it was a leftover menu entry).
  // recipeRef carried for the same recreate-if-missing reason as above. Preserve
  // the batch's `preparedBy` on the given-back row so a later batch-drain (step 3
  // here, or a subsequent promote/re-cook) can still find it by its cook link —
  // without it the give-back row goes orphaned (preparedBy undefined) and a drain
  // that matches on preparedBy misses it, leaving a stray leftover row in stock.
  for (const d of entry.fromRecipeStock ?? []) {
    const give = (Number(d.servings) || 0) * keepFactor;
    if (give <= 0) continue; // fully eaten — nothing to return
    ops.push({
      type: 'returnRecipeStock',
      stockRef: d.stockRef,
      servings: give,
      recipeRef: entry.recipeRef,
    });
    depositToPool(pools.recipes, entry.recipeRef, {
      stockRef: d.stockRef,
      servings: give,
      expiry: null,
      preparedBy: entry.preparedBy,
    });
  }

  // 2b) Return any SIBLING-owned recipe-stock this entry drew while sourcing a
  // SUB-recipe (usesBatches). These are a different recipe from `entry.recipeRef`
  // (they belong to a sub-recipe component), so return them under each batch's own
  // recipeRef and preserve its preparedBy so the owning cook still finds the row.
  for (const b of entry.usesBatches ?? []) {
    const give = (Number(b.servings) || 0) * keepFactor;
    if (give <= 0) continue;
    ops.push({
      type: 'returnRecipeStock',
      stockRef: b.stockRef,
      servings: give,
      recipeRef: b.recipeRef,
    });
    depositToPool(pools.recipes, b.recipeRef, {
      stockRef: b.stockRef,
      servings: give,
      expiry: null,
      preparedBy: b.preparedBy,
    });
  }

  // 3) Drain every recipe-stock BATCH this entry's sourcing deposited — the
  // top-level surplus AND any nested sub-recipe batches cooked to satisfy it
  // (e.g. removing "Shepherd's Pie on Toast" that cooked a Shepherd's Pie batch
  // must un-cook that batch too). Match by the exact stockRef recorded at source
  // time so the give-back is precise, scaling still-pooled rows down by whatever
  // remains un-drawn. Without this, batches linger and a later re-cook
  // double-counts.
  if (Array.isArray(entry.cookedBatches)) {
    for (const batch of entry.cookedBatches) {
      const bucket = pools.recipes.get(batch.recipeRef);
      if (!bucket) continue;
      // Drain every pooled row belonging to this batch. Match by `preparedBy`
      // (the batch owner) rather than the exact stockRef, because a later
      // leftover entry's give-back deposits a NEW row that keeps the batch's
      // preparedBy but has a fresh stockRef — matching by stockRef alone would
      // miss it and leave a stray serving (the "orphaned give-back" hazard).
      for (const row of bucket.rows) {
        const owns = row.preparedBy === batch.preparedBy || row.stockRef === batch.stockRef;
        if (!owns || row.servings <= 0) continue;
        ops.push({
          type: 'consumeRecipeStock',
          stockRef: row.stockRef,
          recipeRef: batch.recipeRef,
          servings: row.servings,
        });
        row.servings = 0;
      }
      bucket.rows = bucket.rows.filter((r) => r.servings > 0);
    }
  } else if (leftoverServingsOf(entry) > 0) {
    // Legacy entries (pre-R4, no cookedBatches) deposited a single top-level
    // batch linked by preparedBy === menuEntryId — drain it the old way so
    // in-flight edits of older menus stay conserved.
    const bucket = pools.recipes.get(entry.recipeRef);
    for (const row of bucket?.rows ?? []) {
      if (row.preparedBy !== entry.menuEntryId || row.servings <= 0) continue;
      ops.push({
        type: 'consumeRecipeStock',
        stockRef: row.stockRef,
        recipeRef: entry.recipeRef,
        servings: row.servings,
      });
      row.servings = 0;
    }
    if (bucket) bucket.rows = bucket.rows.filter((r) => r.servings > 0);
  }

  return { ops };
}

/**
 * Can this entry's serving count be changed directly (the edit-mode serves
 * stepper)? Deliberately limited to the simple case — a PLANNED, NO-PREP recipe
 * entry whose recipe makes exactly ONE serving per batch from INGREDIENTS ONLY —
 * where every serve is an independent batch that leaves no leftovers, so changing
 * the count can never affect another entry. Excluded:
 *   • quick-energy entries (no recipe/stock)
 *   • prep entries — the recipe requires prep, or the entry's sourcing cooked a
 *     prep sub-recipe (derived `requiresPrep`), or it's already prepared
 *   • leftover entries (they eat another entry's batch)
 *   • multi-serve batches (servingsMade ≠ 1, or a kept-serving override)
 *   • recipes with sub-recipe components (a sub-recipe batch can make several
 *     serves → leftovers other entries may rely on)
 * PURE.
 */
export function canChangeServings(entry, recipe) {
  if (!entry || !recipe || entry.kind === 'quick') return false;
  if (entry.recipeRef !== recipe.itemId) return false;
  if (recipe.requiresPrep !== false || entry.requiresPrep || isPrepared(entry)) return false;
  if ((entry.fromRecipeStock ?? []).length > 0 || entry.preparedBy) return false;
  if (madeFor(recipe, entry.cookOverrides) !== 1) return false;
  return (recipe.components ?? []).every((c) => componentKind(c) !== 'recipe');
}

/**
 * Change an eligible entry's serving count (see canChangeServings) to `servings`
 * (a whole number, clamped to ≥ 1 and ≥ what's already eaten). Composes the two
 * tested primitives: give the entry's WHOLE planned provenance back to the pools
 * (removeEntry, treating it as uneaten so every recorded draw returns), then
 * re-source the full new count (sourceEntry). The provenance therefore always
 * covers all `servings` (eaten included) — the invariant removeEntry's
 * remaining-share give-back relies on — and the net pool effect is exactly the
 * serve delta, in whole servings. The entry keeps its identity (menuEntryId,
 * slot, eaten, …). Ineligible entries / no-op changes return the entry unchanged
 * with no ops. PURE except it mutates `pools`.
 *
 * @returns {{ entry, ops, unsourced }} the updated entry (a new object) + the
 *   give-back/source ops to append to the edit session's op log.
 */
export function setEntryServings({
  entry,
  recipe,
  servings,
  pools,
  ingredientsById,
  recipesById,
  date,
  genStockId = () => crypto.randomUUID(),
}) {
  const unchanged = { entry, ops: [], unsourced: [] };
  if (!canChangeServings(entry, recipe)) return unchanged;
  const eaten = Math.max(0, Number(entry.eaten) || 0);
  const target = Math.max(1, eaten, Math.floor(Number(servings) || 0));
  if (target === (Number(entry.servings) || 0)) return unchanged;

  const { ops: giveBack } = removeEntry({ entry: { ...entry, eaten: 0 }, pools });
  const {
    entry: fresh,
    ops,
    unsourced,
  } = sourceEntry({
    recipe,
    servings: target,
    pools,
    ingredientsById,
    recipesById,
    date,
    genId: () => entry.menuEntryId,
    genStockId,
  });
  // Drop the old sourcing provenance; keep the entry's identity + user state.
  const keep = { ...entry };
  delete keep.ingredients;
  delete keep.cooks;
  delete keep.giveAwayWarnings;
  return { entry: { ...keep, ...fresh }, ops: [...giveBack, ...ops], unsourced };
}

/**
 * Resolve a preparedEntryRemoval prompt (from removeEntry) once the caller has
 * chosen. PURE (mutates the given pools only). The removed prepared entry's OWN
 * ingredient give-back has already been emitted by removeEntry; this handles the
 * leftover menu entries that consumed its batch.
 *
 *   choice 'delete'  — the leftovers are gone: clear every leftover menu entry's
 *                      slot. They never consumed real stock (they ate the batch
 *                      being removed), so there's nothing to give back — we just
 *                      emit menuPatches that blank those entries.
 *
 *   choice 'promote' — prepare the batch on a later day instead. The FIRST
 *                      leftover menu entry becomes the new prepared entry: it is
 *                      re-sourced via sourceEntry (drawing/buying its whole batch
 *                      and depositing a fresh leftover batch), and the REMAINING
 *                      leftover menu entries are re-linked to its menuEntryId.
 *                      Because the original prepared entry's own serving is now
 *                      skipped, the new batch still makes `servingsMade` servings
 *                      but feeds one fewer entry — that extra serving is naturally
 *                      retained as recipe-stock (sourceEntry deposits made − 1).
 *
 * @param choice          'promote' | 'delete'
 * @param leftoverEntries the menu entries that consumed the removed batch, in menu
 *                        order (date asc). Each carries { date, recipeRef, … }.
 * @param recipe          the recipe record for the batch (to re-source on promote)
 * @returns { ops, menuPatches } — menuPatches = [{ date, entry|null }] the caller
 *   writes back (null = clear that day's slot for this batch).
 */
export function resolvePreparedEntryRemoval({
  choice,
  leftoverEntries = [],
  recipe,
  pools,
  ingredientsById,
  recipesById,
  genId = () => crypto.randomUUID(),
  genStockId = genId,
  leftoverShelfLifeDays,
}) {
  if (choice === 'delete') {
    // No stock to give back (they ate the now-removed batch); just clear slots.
    return { ops: [], menuPatches: leftoverEntries.map((e) => ({ date: e.date, entry: null })) };
  }

  if (choice === 'promote') {
    if (leftoverEntries.length === 0) return { ops: [], menuPatches: [] };
    const [first, ...rest] = leftoverEntries;
    // Re-source the first leftover menu entry as the new prepared entry (eats 1,
    // makes the batch).
    const { entry: preparedEntry, ops } = sourceEntry({
      recipe,
      servings: 1,
      pools,
      ingredientsById,
      recipesById,
      date: first.date,
      genId,
      genStockId,
      leftoverShelfLifeDays,
    });
    const menuPatches = [{ date: first.date, entry: preparedEntry }];
    // Re-link the remaining leftover menu entries to the new prepared entry.
    for (const leftoverEntry of rest) {
      menuPatches.push({
        date: leftoverEntry.date,
        entry: { ...leftoverEntry, preparedBy: preparedEntry.menuEntryId, requiresPrep: false },
      });
    }
    return { ops, menuPatches };
  }

  return { ops: [], menuPatches: [] };
}

/**
 * Replace a PREPARED entry (and the leftover entries that eat its batch) with a
 * different recipe. The original cook day stays the cook day; the new recipe is
 * sourced there (make/buy), and its leftovers fill the existing leftover slots in
 * chronological order. If the new recipe makes FEWER servings than there are
 * slots, the surplus slots are deleted; if it makes more, the extra leftovers
 * simply remain pooled as recipe-stock.
 *
 * PURE. Returns:
 *   { ops, menuPatches: [{ date, entry }], deleted: [{ date, menuEntryId }] }
 * menuPatches replace existing entries (matched by menuEntryId on the same day);
 * `deleted` lists leftover slots to remove. The first patch is always the new
 * prepared entry on `preparedDate`.
 *
 * (Definition below, after resolveCookRemoval.)
 */

/**
 * UNIFIED resolution for removing a COOK and dealing with every dependent of its
 * batch in one pass — the same mechanism whether a dependent is a SAME-recipe
 * leftover eater (`link:'leftover'`) or a DIFFERENT recipe that ate the batch as a
 * SUB-recipe (`link:'subrecipe'`). Replaces the old split of
 * `resolvePreparedEntryRemoval` (leftovers) + ad-hoc recursive-consumer handling.
 *
 * decisions = { action: 'promote' | 'deleteAll' }
 *
 * Semantics (all-or-nothing — see the menu-planner notes):
 *  - 'deleteAll' → remove the cook AND every dependent (each dependent slot is
 *    deleted; the cook's batch is drained by its own removal).
 *  - 'promote' → the CHRONOLOGICALLY-NEXT dependent (dependents[0], any kind)
 *    becomes the new batch owner. It's re-sourced fresh on its own day cooking its
 *    OWN recipe (a leftover dependent cooks recipe A; a sub-recipe consumer cooks
 *    its recipe B, which cooks a full A batch internally) — either way a full A
 *    batch is deposited. Every OTHER dependent is then re-sourced against that
 *    pool: leftover dependents draw A (linked to the new owner), sub-recipe
 *    consumers draw A for their component. Stale usesBatches are discarded so
 *    nobody re-draws the torn-down batch.
 *
 * PURE. Returns { ops, menuPatches:[{date,entry|null}], deleted:[{date,menuEntryId}],
 * promotedEntry|null }. menuPatches with entry:null mean "remove that slot".
 */
export function resolveCookRemoval({
  cook,
  dependents = [],
  decisions = {},
  pools,
  ingredientsById,
  recipesById,
  genId = () => crypto.randomUUID(),
  genStockId = genId,
  leftoverShelfLifeDays,
}) {
  const ops = [];
  const menuPatches = [];
  const deleted = [];
  const action = decisions.action ?? 'deleteAll';

  // Remove the cook first (drains its batch, gives back its ingredients).
  ops.push(...removeEntry({ entry: cook, pools, leftoverEntries: [] }).ops);

  if (action !== 'promote' || dependents.length === 0) {
    // deleteAll: every dependent slot goes.
    for (const d of dependents) deleted.push({ date: d.date, menuEntryId: d.menuEntryId });
    return { ops, menuPatches, deleted, promotedEntry: null };
  }

  // promote: dependents are menu-ordered; the first becomes the new owner and is
  // re-sourced cooking its OWN recipe (which cooks a full A batch). Every other
  // dependent then re-sources against the resulting pool, in order.
  let promotedEntry = null;
  for (let i = 0; i < dependents.length; i += 1) {
    const d = dependents[i];
    const recipe = recipesById?.get?.(d.recipeRef);
    if (!recipe) {
      // Unknown recipe — can't re-source; drop the slot.
      deleted.push({ date: d.date, menuEntryId: d.menuEntryId });
      continue;
    }
    // Re-source fresh (discarding stale usesBatches — the old batch is gone). The
    // owner (i===0) cooks its own batch; the rest draw from the pool it filled.
    // The owner takes over the removed cook's batch, so it inherits its kept-yield
    // overrides (keyed by recipeRef, so they still apply when the owner cooks the
    // batch recipe as a sub-recipe).
    const { entry, ops: srcOps } = addEntry({
      recipe,
      pools,
      ingredientsById,
      recipesById,
      date: d.date,
      genId,
      genStockId,
      leftoverShelfLifeDays,
      cookOverrides: i === 0 ? (cook.cookOverrides ?? null) : null,
    });
    ops.push(...srcOps);
    menuPatches.push({
      date: d.date,
      entry: { ...entry, menuEntryId: d.menuEntryId, slot: d.slot },
    });
    if (i === 0) promotedEntry = entry;
  }

  return { ops, menuPatches, deleted, promotedEntry };
}

export function replacePreparedEntry({
  preparedEntry,
  preparedDate,
  leftoverEntries = [],
  newRecipe,
  pools,
  ingredientsById,
  recipesById,
  genId = () => crypto.randomUUID(),
  genStockId = genId,
  leftoverShelfLifeDays,
}) {
  const ops = [];

  // 1) Give back the OLD prepared entry: returns its ingredients and drains the
  // batch it deposited (so the new batch doesn't double-count). We pass no
  // leftoverEntries so it never prompts — we handle the dependents ourselves.
  const removal = removeEntry({ entry: preparedEntry, pools, leftoverEntries: [] });
  ops.push(...removal.ops);

  // The old leftover entries ate that (now-removed) batch; there's nothing else
  // to give back for them — the batch never really existed once the cook is gone.

  // 2) Source the NEW recipe on the original cook day (eat 1, make the batch).
  // Re-cooking the SAME recipe (a batch-chain move) keeps the cook's kept-yield
  // overrides; a different recipe starts from its own defaults.
  const cookOverrides =
    newRecipe.itemId === preparedEntry.recipeRef ? (preparedEntry.cookOverrides ?? null) : null;
  const { entry: newPrepared, ops: srcOps } = sourceEntry({
    recipe: newRecipe,
    servings: 1,
    pools,
    ingredientsById,
    recipesById,
    date: preparedDate,
    genId,
    genStockId,
    leftoverShelfLifeDays,
    cookOverrides,
  });
  withCookOverrides(newPrepared, cookOverrides);
  ops.push(...srcOps);

  const menuPatches = [{ date: preparedDate, entry: newPrepared }];
  const deleted = [];

  // 3) Fill the existing leftover slots chronologically from the new batch.
  const slots = [...leftoverEntries].sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
  );
  for (const slot of slots) {
    const drawn = consumeRecipeStock({ recipe: newRecipe, servings: 1, pools, genId });
    if (drawn.entry) {
      ops.push(...drawn.ops);
      menuPatches.push({
        date: slot.date,
        entry: {
          ...drawn.entry,
          menuEntryId: slot.menuEntryId,
          preparedBy: newPrepared.menuEntryId,
        },
      });
    } else {
      // New batch exhausted — this slot can't be filled, so delete it.
      deleted.push({ date: slot.date, menuEntryId: slot.menuEntryId });
    }
  }

  return { ops, menuPatches, deleted };
}
